import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { projectsAPI, usersAPI, teamsAPI, tasksAPI, sprintsAPI, getFileUrl } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Card, PageHeader, Badge, Button, Modal, Table, Empty, Alert, FormField, SearchInput } from '../components/ui';
import { Plus, Pencil, Trash2, Rocket, X, ChevronRight, ChevronLeft, UserPlus, CheckCircle2, ExternalLink, MoreVertical, CheckCircle, ArrowLeft, Calendar, Users, FileText, BarChart3, Flag, Milestone, ListTodo, Activity, Lock, Tag, DollarSign, Target, Clock, PlusCircle, MessageSquare, CheckSquare } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import './Projects.css';

// Module-load debug: log imported UI components and icons so we can detect undefined imports
// (This runs even if the route is protected and the component isn't rendered.)
// eslint-disable-next-line no-console
console.log('Projects module imports check', {
  // UI
  Card, PageHeader, Badge, Button, Modal, Table, Empty, Alert, FormField, SearchInput,
  // Icons (a subset)
  Plus, Pencil, Trash2, Rocket, X, ChevronRight, ChevronLeft, UserPlus
});

const empty = {
  project_name: '',
  project_code: '',
  description: '',
  project_key: '',
  category: 'Regular Project',
  team: '',
  project_color: '#6c63ff',
  start_date: '',
  end_date: '',
  milestones: [],
  visibility: 'Public - all members',
  default_view: 'Board (Kanban)',
  tags: [],
  priority: 'Medium',
  project_lead_id: '',
  members: [],                                                                              
  estimated_budget: '',
  currency: 'USD ($)',
  client_id: '',
  manager_id: '',
  client_delivery_id: '',
  status: 'Backlog',
  client_feedback: '',
  poc_checklist: {}
};

const POC_SECTIONS = [
  { id: '1', title: '1. Project Information', items: ['Product Name', 'Project Owner', 'Technical Lead', 'Version', 'Start Date', 'End Date', 'POC Objectives', 'Success Criteria'] },
  { id: '2', title: '2. Business Validation', items: ['Problem Statement', 'Target Users', 'Business Goals', 'Expected Benefits', 'Key Features to Validate', 'Risks Identified'] },
  { id: '3', title: '3. Technical Validation', items: ['Technology Stack Selected', 'Architecture Designed', 'Database Selected', 'API Strategy Defined', 'Cloud Environment Prepared', 'Security Approach Identified'] },
  { id: '4', title: '4. UI/UX Prototype', items: ['Wireframes', 'User Flow', 'Responsive Design', 'Navigation', 'Branding', 'Accessibility'] },
  { id: '5', title: '5. Core Functional Modules', items: ['User Authentication', 'Dashboard', 'Role-Based Access Control (RBAC)', 'CRUD Operations', 'Search & Filters', 'Notifications', 'Reports', 'Settings'] },
  { id: '6', title: '6. Backend Development', items: ['API Development', 'Authentication', 'Authorization', 'Business Logic', 'Validation', 'Error Handling'] },
  { id: '7', title: '7. Database', items: ['Database Schema', 'Relationships', 'Sample Data', 'Indexes', 'Constraints', 'Backup Strategy'] },
  { id: '8', title: '8. Third-Party Integrations', items: ['Payment Gateway', 'Email Service', 'SMS Service', 'Cloud Storage', 'Maps', 'AI APIs', 'OAuth Login'] },
  { id: '9', title: '9. Security', items: ['HTTPS', 'JWT/OAuth Authentication', 'Password Encryption', 'Role Permissions', 'API Security', 'Input Validation', 'Audit Logging'] },
  { id: '10', title: '10. Performance Validation', items: ['API Response Time', 'Database Performance', 'Concurrent Users', 'Load Testing', 'Caching Strategy'] },
  { id: '11', title: '11. DevOps', items: ['Git Repository', 'Branch Strategy', 'Docker', 'CI/CD Pipeline', 'Deployment', 'Monitoring'] },
  { id: '12', title: '12. Testing', items: ['Unit Testing', 'Integration Testing', 'API Testing', 'UI Testing', 'Security Testing', 'User Acceptance Testing (UAT)'] },
  { id: '13', title: '13. Documentation', items: ['Architecture Diagram', 'API Documentation', 'Database ER Diagram', 'Deployment Guide', 'User Guide'] },
  { id: '14', title: '14. POC Deliverables', items: ['Working Prototype', 'Source Code', 'API Collection', 'Database Scripts', 'Demo Environment', 'Demo Video', 'Presentation'] },
  { id: '15', title: '15. Success Criteria', items: ['Core features work correctly', 'Performance targets are met', 'Security requirements are validated', 'Technical feasibility is confirmed', 'Positive stakeholder feedback', 'Ready for MVP planning'] }
];

const POC_PHASES = [
  { name: 'Strategy & Business', sectionIds: ['1', '2'] },
  { name: 'Architecture & Design', sectionIds: ['3', '4'] },
  { name: 'Core Development', sectionIds: ['5', '6', '7'] },
  { name: 'Integrations & Security', sectionIds: ['8', '9'] },
  { name: 'Performance & DevOps', sectionIds: ['10', '11', '12'] },
  { name: 'Deliverables & Success', sectionIds: ['13', '14', '15'] },
];

const emptyTask = { task_name: '', description: '', status: 'To Do', priority: 'Medium', project_id: '', assigned_to: '' };

const colorOptions = [
  '#6c63ff', '#06b6d4', '#3b82f6', '#f97316',
  '#ec4899', '#f472b6', '#10b981', '#047857', '#6b7280',
];

const STATUS_OPTIONS = ['Backlog', 'To Do', 'In Progress', 'In Review', 'Done'];

const formatDateShort = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

export default function Projects() {
  const { user: me, canCreate, canUpdate, canDelete } = useAuth();
  // Debug: log imported components/icons to catch any undefined imports causing runtime errors
  // Remove this log after debugging the "Element type is invalid" issue.
  // eslint-disable-next-line no-console
  console.log('UI imports:', { Card, PageHeader, Badge, Button, Modal, Table, Empty, Alert, FormField, SearchInput });
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const pendingProjectId = useRef(null);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [groupByCategory, setGroupByCategory] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;
  const [view, setView] = useState('list');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [showLeadDropdown, setShowLeadDropdown] = useState(false);
  const [showMembersDropdown, setShowMembersDropdown] = useState(false);
  const [completionTarget, setCompletionTarget] = useState(null);
  const [completionForm, setCompletionForm] = useState({ completion_url: '', completion_note: '' });
  const [completionSaving, setCompletionSaving] = useState(false);
  const [projectTasks, setProjectTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [detailTab, setDetailTab] = useState('overview');
  const [pocStep, setPocStep] = useState(0);
  const [pocSaving, setPocSaving] = useState(null); // null | 'saving' | 'saved'
  const pocSaveTimerRef = useRef(null);
  const [sprints, setSprints] = useState([]);

  const [taskModal, setTaskModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [taskForm, setTaskForm] = useState(emptyTask);
  const [taskError, setTaskError] = useState('');
  const [taskSaving, setTaskSaving] = useState(false);
  const [descriptionModal, setDescriptionModal] = useState(false);

  const [workSession, setWorkSession] = useState({ activeSession: null, totalMinutes: 0 });
  const [sessionLoading, setSessionLoading] = useState(false);
  const [sessionSummary, setSessionSummary] = useState([]);
  const [elapsedActiveSeconds, setElapsedActiveSeconds] = useState(0);

  useEffect(() => {
    let interval = null;
    if (workSession.activeSession) {
      const startTime = new Date(workSession.activeSession.start_time).getTime();
      const updateElapsed = () => {
        const now = new Date().getTime();
        setElapsedActiveSeconds(Math.floor((now - startTime) / 1000));
      };
      updateElapsed();
      interval = setInterval(updateElapsed, 1000); // update every second
    } else {
      setElapsedActiveSeconds(0);
    }
    return () => { if (interval) clearInterval(interval); };
  }, [workSession.activeSession]);

  const handleTaskSave = async (e) => {
    e.preventDefault();
    setTaskSaving(true); setTaskError('');
    try {
      if (editingTask) await tasksAPI.update(editingTask.task_id, { ...taskForm, project_id: editing.project_id });
      else await tasksAPI.create({ ...taskForm, project_id: editing.project_id });
      setTaskModal(false);
      const tasksRes = await tasksAPI.list({ project_id: editing.project_id });
      setProjectTasks(tasksRes.data.data || []);
    } catch (err) {
      setTaskError(err.response?.data?.message || 'Error saving task');
    } finally { setTaskSaving(false); }
  };

  const handleTaskDelete = async (id) => {
    if (!window.confirm('Delete this task?')) return;
    await tasksAPI.delete(id);
    const tasksRes = await tasksAPI.list({ project_id: editing.project_id });
    setProjectTasks(tasksRes.data.data || []);
  };

  const handleStartWorkSession = async () => {
    if (!editing) return;
    setSessionLoading(true);
    try {
      await projectsAPI.startWorkSession(editing.project_id);
      const res = await projectsAPI.getWorkSessionStatus(editing.project_id);
      setWorkSession(res.data.data);
    } catch (err) {
      alert(err.response?.data?.message || 'Error starting work session');
    } finally {
      setSessionLoading(false);
    }
  };

  const handleStopWorkSession = async () => {
    if (!editing) return;
    setSessionLoading(true);
    try {
      await projectsAPI.stopWorkSession(editing.project_id);
      const res = await projectsAPI.getWorkSessionStatus(editing.project_id);
      setWorkSession(res.data.data);
    } catch (err) {
      alert(err.response?.data?.message || 'Error stopping work session');
    } finally {
      setSessionLoading(false);
    }
  };

  const load = () => {
    setLoading(true);
    const requests = [projectsAPI.list(), teamsAPI.list()];
    if (canCreate) {
      requests.push(usersAPI.list());
    }
    Promise.all(requests)
      .then(([p, t, u]) => {
        setProjects(p.data.data || []);
        setTeams(t.data.data || []);
        if (canCreate) {
          setUsers(u?.data?.data || []);
        }
      })
      .catch(err => {
        console.error('Error fetching data:', err);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [canCreate]);

  useEffect(() => {
    const id = searchParams.get('project_id');
    if (id) pendingProjectId.current = id;
  }, [searchParams]);

  useEffect(() => {
    if (loading || !projects.length || !pendingProjectId.current) return;
    const project = projects.find(p => String(p.project_id) === String(pendingProjectId.current));
    if (project) {
      viewDetails(project);
      pendingProjectId.current = null;
      const next = new URLSearchParams(searchParams);
      next.delete('project_id');
      setSearchParams(next, { replace: true });
    }
  }, [loading, projects]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter, categoryFilter]);

  const openCreate = () => {
    setEditing(null);
    setForm(empty);
    setError('');
    setTagInput('');
    setShowLeadDropdown(false);
    setShowMembersDropdown(false);
    setView('create');
  };

  const viewDetails = async (p) => {
    setEditing(p);
    setView('details');
    setDetailTab('overview');
    setPocStep(0);
    setLoadingTasks(true);
    setSessionLoading(true);
    try {
      const pId = p.project_id;
      const promises = [
        tasksAPI.list({ project_id: pId }),
        sprintsAPI.list().catch(() => ({ data: { data: [] } })),
        projectsAPI.getWorkSessionStatus(pId).catch(() => ({ data: { data: { activeSession: null, totalMinutes: 0 } } })),
      ];
      if (me?.role === 'Admin' || me?.role === 'Manager') {
        promises.push(projectsAPI.getWorkSessionSummary(pId).catch(() => ({ data: { data: [] } })));
      } else {
        promises.push(Promise.resolve({ data: { data: [] } }));
      }
      const [tasksRes, sprintsRes, sessionRes, summaryRes] = await Promise.all(promises);
      setProjectTasks(tasksRes.data.data || []);
      setSprints(sprintsRes.data.data || []);
      setWorkSession(sessionRes.data.data || { activeSession: null, totalMinutes: 0 });
      setSessionSummary(summaryRes.data.data || []);
    } catch (err) {
      setProjectTasks([]);
      setSprints([]);
      setWorkSession({ activeSession: null, totalMinutes: 0 });
      setSessionSummary([]);
    } finally {
      setLoadingTasks(false);
      setSessionLoading(false);
    }
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      project_name: p.project_name || '',
      project_code: p.project_code || '',
      description: p.description || '',
      project_key: p.project_key || '',
      team: p.team || '',
      project_color: p.project_color || '#6c63ff',
      start_date: p.start_date || '',
      end_date: p.end_date || '',
      milestones: (() => { try { return typeof p.milestones === 'string' ? JSON.parse(p.milestones || '[]') : (Array.isArray(p.milestones) ? p.milestones : []); } catch { return []; } })(),
      visibility: p.visibility || 'Public - all members',
      default_view: p.default_view || 'Board (Kanban)',
      tags: (() => { try { return typeof p.tags === 'string' ? JSON.parse(p.tags || '[]') : (Array.isArray(p.tags) ? p.tags : []); } catch { return []; } })(),
      priority: p.priority || 'Medium',
      category: p.category || 'Regular Project',
      project_lead_id: p.project_lead_id || p.projectLead?.user_id || '',
      members: (() => { try { return typeof p.members === 'string' ? JSON.parse(p.members || '[]') : (Array.isArray(p.members) ? p.members : []); } catch { return []; } })(),
      estimated_budget: p.estimated_budget || '',
      currency: p.currency || 'USD ($)',
      client_id: p.client_id || p.client?.user_id || '',
      manager_id: p.manager_id || '',
      client_delivery_id: p.client_delivery_id || '',
      status: p.status || 'Backlog',
      client_feedback: p.client_feedback || '',
    });
    setError('');
    setTagInput('');
    setShowLeadDropdown(false);
    setShowMembersDropdown(false);
    setView('edit');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    const payload = {
      ...form,
      project_code: form.project_code?.trim() || null,
      client_id: form.client_id === '' ? null : Number(form.client_id),
      manager_id: form.manager_id === '' ? null : Number(form.manager_id),
      client_delivery_id: form.client_delivery_id === '' ? null : Number(form.client_delivery_id),
      project_lead_id: form.project_lead_id === '' ? null : Number(form.project_lead_id),
      estimated_budget: form.estimated_budget === '' ? null : parseFloat(form.estimated_budget),
      start_date: form.start_date === '' ? null : form.start_date,
      end_date: form.end_date === '' ? null : form.end_date,
    };

    try {
      if (editing) {
        await projectsAPI.update(editing.project_id, payload);
      } else {
        await projectsAPI.create(payload);
      }
      setView('list');
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Error saving project');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this project?')) return;
    try {
      await projectsAPI.delete(id);
      load();
    } catch (err) {
      alert(err.response?.data?.message || 'Error deleting project');
    }
  };

  const handleApproveMilestone = async (milestoneIndex) => {
    if (!editing) return;
    try {
      const response = await projectsAPI.approveMilestone(editing.project_id, milestoneIndex);
      setEditing(response.data.data);
      load();
    } catch (err) {
      alert(err.response?.data?.message || 'Unable to approve this milestone.');
    }
  };

  const filteredProjects = useMemo(() => {
    return projects.filter(p => {
      let match = true;
      if (statusFilter) match = p.status === statusFilter;
      if (categoryFilter && categoryFilter !== 'All Categories') match = match && p.category === categoryFilter;
      if (search) {
        const lower = search.toLowerCase();
        match = match && (p.project_name?.toLowerCase().includes(lower) || p.project_key?.toLowerCase().includes(lower) || p.project_code?.toLowerCase().includes(lower));
      }
      return match;
    });
  }, [projects, search, statusFilter, categoryFilter]);

  const uniqueCategories = useMemo(() => {
    const cats = projects.map(p => p.category).filter(Boolean);
    return ['All Categories', ...new Set(cats)];
  }, [projects]);

  const totalPages = Math.ceil(filteredProjects.length / itemsPerPage);
  const paginatedProjects = filteredProjects.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const projectMemberIds = (project) => {
    if (Array.isArray(project.members)) return project.members.map(Number);
    try { return JSON.parse(project.members || '[]').map(Number); } catch { return []; }
  };

  const isAssignedToProject = (project) =>
    Number(project.project_lead_id) === Number(me?.user_id) || projectMemberIds(project).includes(Number(me?.user_id));

  const openCompletion = (project) => {
    setCompletionTarget(project);
    setCompletionForm({ completion_url: '', completion_note: '' });
    setError('');
  };

  const handleComplete = async (e) => {
    e.preventDefault();
    setCompletionSaving(true);
    setError('');
    try {
      await projectsAPI.complete(completionTarget.project_id, completionForm);
      setCompletionTarget(null);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to complete project');
    } finally {
      setCompletionSaving(false);
    }
  };

  const handleAddTag = () => {
    const cleanTag = tagInput.trim();
    if (cleanTag && !form.tags.includes(cleanTag)) {
      setForm(f => ({ ...f, tags: [...f.tags, cleanTag] }));
      setTagInput('');
    }
  };

  const calculateProgress = () => {
    const fields = [form.project_name, form.project_key, form.team, form.start_date, form.end_date];
    const filled = fields.filter(val => val && String(val).trim() !== '').length;
    return { filled, total: 5, percentage: (filled / 5) * 100 };
  };

  const progress = calculateProgress();

  const columns = [
    {
      key: 'project_name',
      label: 'Name',
      minWidth: 240,
      render: r => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{
            width: 10, height: 10, borderRadius: '50%', display: 'inline-block', flexShrink: 0,
            backgroundColor: r.project_color || '#6c63ff', boxShadow: `0 0 6px ${r.project_color || '#6c63ff'}40`
          }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontWeight: 600, color: 'var(--text)' }}>{r.project_name}</span>
            {r.project_key && (
              <span style={{
                fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', background: 'var(--bg-hover)',
                border: '1px solid var(--border-bright)', padding: '2px 6px', borderRadius: 4,
                textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap',
                alignSelf: 'flex-start'
              }}>
                {r.project_key}
              </span>
            )}
          </div>
        </div>
      )
    },
    {
      key: 'project_code',
      label: 'Code',
      minWidth: 150,
      render: r => r.project_code ? (
        <span style={{
          fontFamily: 'monospace', fontSize: 11, fontWeight: 700, color: 'var(--accent)',
          background: 'rgba(108,99,255,0.1)', border: '1px solid rgba(108,99,255,0.25)',
          padding: '3px 8px', borderRadius: 6, letterSpacing: '0.04em', textTransform: 'uppercase',
          whiteSpace: 'nowrap', display: 'inline-block'
        }}>
          {r.project_code}
        </span>
      ) : <span style={{ color: 'var(--text-dim)', fontSize: 12 }}>—</span>
    },
    { key: 'client', label: 'Client', minWidth: 160, render: r => r.client?.name || r.client?.company || (r.client_id ? `#${r.client_id}` : '—') },
    { key: 'team', label: 'Team', minWidth: 140, render: r => r.team || '—' },
    { key: 'category', label: 'Category', minWidth: 170, render: r => r.category ? <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 12, border: '1px solid var(--border)', background: 'var(--bg-hover)', color: 'var(--text-muted)' }}>{r.category}</span> : '—' },
    {
      key: 'projectLead',
      label: 'Lead',
      minWidth: 170,
      render: r => r.projectLead?.name ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{
            width: 24, height: 24, borderRadius: '50%', background: 'rgba(108,99,255,0.2)',
            color: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 700, fontSize: 10
          }}>
            {r.projectLead.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
          </div>
          <span style={{ fontSize: 12 }}>{r.projectLead.name}</span>
        </div>
      ) : '-'
    },
    { key: 'priority', label: 'Priority', minWidth: 130, render: r => r.priority ? <Badge value={r.priority} /> : '-' },
    { key: 'status', label: 'Status', minWidth: 140, render: r => <Badge value={r.status} /> },
    {
      key: 'createdAt',
      label: 'Created Date',
      minWidth: 150,
      render: r => <span style={{ whiteSpace: 'nowrap', fontSize: 12 }}>{formatDateShort(r.createdAt)}</span>
    },
    {
      key: 'creator',
      label: 'Created By',
      minWidth: 210,
      render: r => r.creator?.name ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{
            width: 24, height: 24, borderRadius: '50%', background: 'rgba(108,99,255,0.2)',
            color: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 700, fontSize: 10, flexShrink: 0
          }}>
            {r.creator.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, whiteSpace: 'nowrap' }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>{r.creator.name}</span>
            {r.creator.role && (
              <span style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 500 }}>{r.creator.role}</span>
            )}
          </div>
        </div>
      ) : '—'
    },
    { key: 'client_feedback', label: 'Client Feedback', minWidth: 240, render: r => r.client_feedback ? (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, maxWidth: 220 }}>
        <MessageSquare size={13} style={{ color: 'var(--accent)', flexShrink: 0 }} />
        <span style={{ fontSize: 12, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.client_feedback}</span>
      </div>
    ) : <span style={{ color: 'var(--text-dim)', fontSize: 12 }}>—</span> },
    { key: 'proof', label: 'Git proof', minWidth: 130, render: r => r.completion_url ? (
      <a href={getFileUrl(r.completion_url)} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ color: 'var(--accent)', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
        View <ExternalLink size={12} />
      </a>
    ) : '—' },
    ...((me?.role === 'Admin' || me?.role === 'Client') ? [{
      key: 'budget',
      label: 'Budget',
      minWidth: 140,
      render: r => r.estimated_budget ? (
        <span style={{ fontWeight: 500 }}>
          {r.currency ? r.currency.split(' ')[0] : '$'}{Number(r.estimated_budget).toLocaleString()}
        </span>
      ) : (r.estimated_budget === 0 ? '$0' : '—')
    }] : []),
    { key: 'start_date', label: 'Start', minWidth: 130, render: r => <span style={{ whiteSpace: 'nowrap' }}>{r.start_date || '—'}</span> },
    { key: 'end_date', label: 'End', minWidth: 130, render: r => <span style={{ whiteSpace: 'nowrap' }}>{r.end_date || '—'}</span> },
    {
      key: 'actions',
      label: '',
      minWidth: 160,
      render: r => (
        <div style={{ display: 'flex', gap: 8 }}>
          {isAssignedToProject(r) && r.status !== 'Done' && (
            <Button size="sm" onClick={e => { e.stopPropagation(); openCompletion(r); }}><CheckCircle2 size={13} /> Complete</Button>
          )}
          <Button size="sm" variant="secondary" onClick={e => { e.stopPropagation(); navigate(`/app/projects/${r.project_id}/dashboard`); }}>
            <BarChart3 size={13} />
          </Button>
          {canUpdate && (
            <Button size="sm" variant="ghost" onClick={e => { e.stopPropagation(); openEdit(r); }}>
              <Pencil size={13} />
            </Button>
          )}
          {canDelete && (
            <Button size="sm" variant="danger" onClick={e => { e.stopPropagation(); handleDelete(r.project_id); }}>
              <Trash2 size={13} />
            </Button>
          )}
        </div>
      )
    }
  ];

  const leadUser = users.find(u => u.user_id === Number(form.project_lead_id));
  const memberUsers = users.filter(u => form.members.includes(u.user_id));

  /* â”€â”€ Shared inline-style helpers â”€â”€ */
  const grid2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 };
  const grid3 = { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 24 };
  const pillBtn = (active, activeColor = 'var(--accent)') => ({
    flex: 1, textAlign: 'center', padding: '8px 0', fontSize: 12, fontWeight: 600,
    borderRadius: 'var(--radius-sm)', cursor: 'pointer', transition: 'all 0.15s',
    border: active ? `1px solid ${activeColor}` : '1px solid var(--border-bright)',
    background: active ? `${activeColor}18` : 'transparent',
    color: active ? activeColor : 'var(--text-muted)',
  });
  const smallBtn = {
    fontSize: 10, fontWeight: 600, color: 'var(--text)',
    border: '1px solid var(--border-bright)', padding: '6px 10px',
    borderRadius: 'var(--radius-sm)', background: 'var(--bg-hover)',
    cursor: 'pointer', transition: 'all 0.15s', display: 'inline-flex',
    alignItems: 'center', gap: 4,
  };

  const toggleStatusFilter = (s) => setStatusFilter(prev => prev === s ? '' : s);

  // Status chart
  const total = projects.length;
  const PIE_R = 54;
  const PIE_CX = 70;
  const PIE_CY = 70;
  let cumAngle = -90;
  
  const statusColors = {
    'Backlog': 'var(--text-dim)',
    'To Do': 'var(--text-muted)',
    'In Progress': 'var(--accent)',
    'In Review': 'var(--accent-light)',
    'Done': 'var(--accent4)'
  };

  const statusGroups = STATUS_OPTIONS.map(status => ({
    status,
    count: projects.filter(p => p.status === status).length,
    color: statusColors[status] || 'var(--text-muted)'
  }));

  const activeGroups = statusGroups.filter(g => g.count > 0);
  const pieSlices = activeGroups.map(g => {
    const pct = total > 0 ? g.count / total : 0;
    const startAngle = cumAngle;
    cumAngle += pct * 360;
    const endAngle = cumAngle;
    const large = pct > 0.5 ? 1 : 0;
    const toRad = deg => (deg * Math.PI) / 180;
    const sx = PIE_CX + PIE_R * Math.cos(toRad(startAngle));
    const sy = PIE_CY + PIE_R * Math.sin(toRad(startAngle));
    const ex = PIE_CX + PIE_R * Math.cos(toRad(endAngle));
    const ey = PIE_CY + PIE_R * Math.sin(toRad(endAngle));
    return { ...g, pct, d: `M ${PIE_CX} ${PIE_CY} L ${sx} ${sy} A ${PIE_R} ${PIE_R} 0 ${large} 1 ${ex} ${ey} Z` };
  });

  return (
    <div className="animate-in" style={{ padding: '20px 0' }}>
      {view === 'list' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          {/* Header */}
          <div className="hero-header" style={{ padding: '32px 40px', marginBottom: 0, '--hero-color': '#6c63ff' }}>
            <div className="hero-header-content">
              <h1 style={{ fontSize: 32, fontWeight: 800, margin: '0 0 8px 0', letterSpacing: '-0.03em' }}>Projects</h1>
              <p style={{ fontSize: 15, margin: 0, opacity: 0.8, fontWeight: 500 }}>
                {projects.length} total project{projects.length !== 1 ? 's' : ''} - Managing delivery & execution
              </p>
            </div>
          </div>

          <div className="projects-page__stats">
            <button
              type="button"
              className={`projects-page__stat modern-card ${statusFilter === '' ? 'projects-page__stat--active' : ''}`}
              style={{ '--stat-color': 'var(--accent)', padding: '20px 24px' }}
              onClick={() => setStatusFilter('')}
            >
              <div className="projects-page__stat-glow" />
              <div className="projects-page__stat-label">Total Projects</div>
              <div className="projects-page__stat-value">{projects.length}</div>
              <div className="projects-page__stat-bar">
                <div className="projects-page__stat-bar-fill" style={{ width: '100%' }} />
              </div>
            </button>
            {statusGroups.filter(g => g.status !== 'Done' && g.status !== 'Backlog').slice(0, 4).map(g => (
              <button
                key={g.status}
                type="button"
                className={`projects-page__stat modern-card ${statusFilter === g.status ? 'projects-page__stat--active' : ''}`}
                style={{ '--stat-color': g.color, padding: '20px 24px' }}
                onClick={() => toggleStatusFilter(g.status)}
              >
                <div className="projects-page__stat-glow" />
                <div className="projects-page__stat-label">{g.status}</div>
                <div className="projects-page__stat-value">{g.count}</div>
                <div className="projects-page__stat-bar">
                  <div className="projects-page__stat-bar-fill" style={{ width: total ? `${(g.count / total) * 100}%` : '0%' }} />
                </div>
              </button>
            ))}
            <button
              type="button"
              className={`projects-page__stat modern-card ${statusFilter === 'Done' ? 'projects-page__stat--active' : ''}`}
              style={{ '--stat-color': 'var(--accent3)', padding: '20px 24px' }}
              onClick={() => toggleStatusFilter('Done')}
            >
              <div className="projects-page__stat-glow" />
              <div className="projects-page__stat-label">Completed</div>
              <div className="projects-page__stat-value">{projects.filter(p => p.status === 'Done').length}</div>
              <div className="projects-page__stat-bar">
                <div className="projects-page__stat-bar-fill" style={{ width: total ? `${(projects.filter(p => p.status === 'Done').length / total) * 100}%` : '0%' }} />
              </div>
            </button>
          </div>

          <div className="glass-panel" style={{ overflow: 'hidden' }}>
            <div className="projects-page__toolbar-inner" style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)' }}>
              <div className="projects-page__chart-panel">
                <div className="projects-page__chart-wrap">
                  <svg width={140} height={140} aria-hidden="true">
                    {pieSlices.map(s => (
                      <path
                        key={s.status}
                        d={s.d}
                        fill={s.color}
                        opacity={statusFilter === s.status ? 1 : statusFilter ? 0.35 : 0.9}
                        stroke="var(--bg-card)"
                        strokeWidth={2}
                        style={{ cursor: 'pointer', transition: 'opacity 0.2s' }}
                        onClick={() => toggleStatusFilter(s.status)}
                      />
                    ))}
                    <circle cx={PIE_CX} cy={PIE_CY} r={28} fill="var(--bg-card)" />
                  </svg>
                  <div className="projects-page__chart-center">
                    <span className="projects-page__chart-value">{filteredProjects.length}</span>
                    <span className="projects-page__chart-label">{statusFilter || 'All'}</span>
                  </div>
                </div>
                <div className="projects-page__legend">
                  {activeGroups.map(g => (
                    <div
                      key={g.status}
                      className={`projects-page__legend-item${statusFilter && statusFilter !== g.status ? ' projects-page__legend-item--dim' : ''}`}
                      onClick={() => toggleStatusFilter(g.status)}
                    >
                      <span className="projects-page__legend-dot" style={{ background: g.color }} />
                      <span className="projects-page__legend-name">{g.status}</span>
                      <span className="projects-page__legend-count">{g.count}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="projects-page__controls">
                <div className="projects-page__search-wrap" style={{ display: 'flex', gap: 12, flex: 1 }}>
                  <div style={{ flex: 1, maxWidth: 400 }}>
                    <SearchInput value={search} onChange={setSearch} placeholder="Search projects by name, code, or key..." />
                  </div>
                  <select 
                    value={categoryFilter} 
                    onChange={e => setCategoryFilter(e.target.value)}
                    style={{ padding: '8px 12px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text)' }}
                  >
                    {uniqueCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                  </select>
                </div>
                
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  <Button variant="ghost" onClick={() => setGroupByCategory(!groupByCategory)}>
                    {groupByCategory ? 'Ungroup' : 'Group by Category'}
                  </Button>
                  {canCreate && (
                    <Button onClick={openCreate}>
                      <Plus size={16} /> New Project
                    </Button>
                  )}
                </div>
              </div>
            </div>

            <div style={{ padding: '12px' }}>
              {loading ? (
                <div style={{ padding: 60, textAlign: 'center', color: 'var(--text-muted)' }}>Loading projects...</div>
              ) : filteredProjects.length === 0 ? (
                <div style={{ padding: 60, textAlign: 'center' }}>
                  <Empty message={search || statusFilter ? 'No projects match your search or filter.' : 'No projects found'} />
                </div>
              ) : groupByCategory ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
                  {uniqueCategories.filter(c => c !== 'All Categories').map(cat => {
                    const catProjects = filteredProjects.filter(p => (p.category || 'Regular Project') === cat);
                    if (catProjects.length === 0) return null;
                    return (
                      <div key={cat} style={{ background: 'var(--bg-card)', borderRadius: 12, overflowX: 'auto', border: '1px solid var(--border)' }}>
                        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', background: 'var(--bg-hover)' }}>
                          <h3 style={{ margin: 0, color: 'var(--text)', fontSize: 15 }}>{cat} <span style={{ color: 'var(--text-muted)', fontSize: 13, marginLeft: 8 }}>({catProjects.length})</span></h3>
                        </div>
                        <Table columns={columns} data={catProjects} onRowClick={viewDetails} />
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <div style={{ background: 'var(--bg-card)', borderRadius: 12, overflowX: 'auto', border: '1px solid var(--border)' }}>
                    <Table columns={columns} data={paginatedProjects} onRowClick={viewDetails} />
                  </div>
                  
                  {totalPages > 1 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 8px' }}>
                      <span style={{ fontSize: 13, color: 'var(--text-muted)', fontWeight: 500 }}>
                        Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredProjects.length)} of {filteredProjects.length} projects
                      </span>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <Button 
                          variant="secondary" 
                          size="sm" 
                          onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                          disabled={currentPage === 1}
                          style={{ padding: '6px 12px', height: 'auto' }}
                        >
                          <ChevronLeft size={16} /> Prev
                        </Button>
                        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', margin: '0 4px' }}>
                          Page {currentPage} of {totalPages}
                        </span>
                        <Button 
                          variant="secondary" 
                          size="sm" 
                          onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                          disabled={currentPage === totalPages}
                          style={{ padding: '6px 12px', height: 'auto' }}
                        >
                          Next <ChevronRight size={16} />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : view === 'details' && editing ? (
        (() => {
          let milestones = [];
          try { milestones = typeof editing.milestones === 'string' ? JSON.parse(editing.milestones || '[]') : (Array.isArray(editing.milestones) ? editing.milestones : []); } catch { milestones = []; }
          let tags = [];
          try { tags = typeof editing.tags === 'string' ? JSON.parse(editing.tags || '[]') : (Array.isArray(editing.tags) ? editing.tags : []); } catch { tags = []; }
          const totalTasks = projectTasks.length;
          const doneTasks = projectTasks.filter(t => t.status === 'Done').length;
          const openTasks = totalTasks - doneTasks;
          const inProgressTasks = projectTasks.filter(t => t.status === 'In Progress' || t.status === 'In Review').length;
          const todoTasks = totalTasks - doneTasks - inProgressTasks;
          const completionPct = totalTasks === 0 ? 0 : Math.round((doneTasks / totalTasks) * 100);
          const blockerTasks = projectTasks.filter(t => t.priority === 'Critical').length;
          const budgetNum = editing.estimated_budget ? Number(editing.estimated_budget) : 0;
          const currencySymbol = editing.currency ? editing.currency.split(' ')[0] : 'USD';
          const budgetUsed = Math.round(budgetNum * 0.32); // simulated usage
          const budgetRemaining = budgetNum - budgetUsed;
          const membersArr = typeof editing.members === 'string' ? JSON.parse(editing.members || '[]') : (editing.members || []);
          const detailMemberUsers = users.filter(u => membersArr.includes(u.user_id));
          const priorityColorMap = { Low: '#34d399', Medium: '#fbbf24', High: '#f87171' };
          const statusColorMap = { Done: 'var(--accent3)', 'In Progress': '#6c63ff', Pending: 'var(--text-dim)' };

          // Find the active sprint for this project (by date range overlap)
          const today = new Date().toISOString().slice(0, 10);
          const activeSprint = sprints.find(s => s.start_date <= today && s.end_date >= today);

          // Determine milestone status based on date
          const getMilestoneStatus = (m) => {
            if (m.status) return m.status;
            const mDate = new Date(m.date);
            const now = new Date();
            
            // If completed, but client is viewing and it's not approved, fallback to 'In Progress'
            const isCompleted = mDate < now;
            if (isCompleted) {
              if (user?.role === 'Client' && !m.approved && !m.manager_approved) {
                return 'In Progress'; // Hide completed status from client until manager approves
              }
              return 'Done';
            }
            
            // Check if it's within 30 days
            const diff = (mDate - now) / (1000 * 60 * 60 * 24);
            if (diff <= 30) return 'In Progress';
            return 'Pending';
          };

          const formatDateShort = (d) => {
            if (!d) return '';
            return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
          };

          const donutData = totalTasks === 0
            ? [{ name: 'Empty', value: 1, fill: 'var(--border-bright)' }]
            : [
                { name: 'Done', value: doneTasks, fill: 'var(--accent3)' },
                { name: 'In Progress', value: inProgressTasks, fill: '#6c63ff' },
                { name: 'To Do', value: todoTasks, fill: 'var(--accent4)' }
              ].filter(d => d.value > 0);

          // Build the roadmap from actual project and milestone dates instead of fixed months.
          const parseRoadmapDate = (value) => {
            if (!value) return null;
            const date = new Date(value);
            return Number.isNaN(date.getTime()) ? null : date;
          };
          const roadmapDates = [
            parseRoadmapDate(editing.start_date),
            parseRoadmapDate(editing.end_date),
            ...milestones.map(m => parseRoadmapDate(m.date)),
          ].filter(Boolean);
          const firstRoadmapDate = roadmapDates.length
            ? new Date(Math.min(...roadmapDates.map(date => date.getTime())))
            : new Date();
          const lastRoadmapDate = roadmapDates.length
            ? new Date(Math.max(...roadmapDates.map(date => date.getTime())))
            : firstRoadmapDate;
          const roadmapStart = new Date(firstRoadmapDate.getFullYear(), firstRoadmapDate.getMonth(), 1);
          const roadmapEnd = new Date(lastRoadmapDate.getFullYear(), lastRoadmapDate.getMonth() + 1, 0);
          const roadmapMonths = [];
          for (let date = new Date(roadmapStart); date <= roadmapEnd; date.setMonth(date.getMonth() + 1)) {
            roadmapMonths.push(new Date(date));
          }
          const roadmapDuration = Math.max(1, roadmapEnd.getTime() - roadmapStart.getTime());
          const getRoadmapPosition = (value) => {
            const date = parseRoadmapDate(value) || roadmapStart;
            return Math.max(0, Math.min(100, ((date.getTime() - roadmapStart.getTime()) / roadmapDuration) * 100));
          };
          const todayPosition = getRoadmapPosition(new Date());
          const isTodayInRoadmap = new Date() >= roadmapStart && new Date() <= roadmapEnd;
          const roadmapLabelWidth = 260;

          const detailCardStyle = {
            background: 'var(--bg-card)',
            borderRadius: 16,
            padding: '20px 24px',
            border: '1px solid var(--border)',
            boxShadow: 'var(--shadow-sm)',
            // Allow unbroken user-entered names/descriptions to stay inside the card.
            minWidth: 0,
            maxWidth: '100%',
            overflowWrap: 'anywhere',
          };

          const sectionHeading = (icon, label) => (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <span style={{ color: 'var(--accent)', display: 'flex' }}>{icon}</span>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>{label}</h3>
            </div>
          );

          const tabs = [
            { key: 'overview', label: 'Overview', icon: <BarChart3 size={14} /> },
            { key: 'roadmap', label: 'Roadmap', icon: <Target size={14} /> },
            { key: 'milestones', label: 'Milestones', icon: <Flag size={14} /> },
            { key: 'tasks', label: 'Tasks', icon: <ListTodo size={14} /> },
            { key: 'activity', label: 'Activity', icon: <Activity size={14} /> },
          ];

          if (me?.role === 'Admin' || me?.role === 'Manager') {
            tabs.push({ key: 'poc', label: 'POC Checklist', icon: <CheckSquare size={14} /> });
          }

          const handlePOCToggle = async (sectionId, item) => {
            let currentChecks = editing.poc_checklist;
            if (typeof currentChecks === 'string') {
              try { currentChecks = JSON.parse(currentChecks); } catch { currentChecks = {}; }
            }
            currentChecks = currentChecks || {};
            const sectionChecks = currentChecks[sectionId] || [];
            
            const isChecked = sectionChecks.includes(item);
            const newSectionChecks = isChecked
              ? sectionChecks.filter(i => i !== item)
              : [...sectionChecks, item];
              
            const newPOCChecklist = {
              ...currentChecks,
              [sectionId]: newSectionChecks
            };
            
            // Optimistic update
            setEditing(prev => ({ ...prev, poc_checklist: newPOCChecklist }));
            setProjects(prev => prev.map(p => p.project_id === editing.project_id ? { ...p, poc_checklist: newPOCChecklist } : p));
            
            // Show saving indicator
            setPocSaving('saving');
            if (pocSaveTimerRef.current) clearTimeout(pocSaveTimerRef.current);
            
            try {
              await projectsAPI.update(editing.project_id, { poc_checklist: newPOCChecklist });
              setPocSaving('saved');
              pocSaveTimerRef.current = setTimeout(() => setPocSaving(null), 2500);
            } catch (err) {
              setPocSaving('error');
              console.error('Failed to save POC checklist update', err);
              pocSaveTimerRef.current = setTimeout(() => setPocSaving(null), 3000);
            }
          };

          return (
            <div className="animate-in" style={{ width: '100%', maxWidth: 1200, minWidth: 0, margin: '0 auto', padding: '20px 0', minHeight: '100vh', fontFamily: 'var(--font-body)', overflowWrap: 'anywhere' }}>
              {/* â•â•â• HEADER â•â•â• */}
              <div className="hero-header" style={{ marginBottom: 30, '--hero-color': editing.project_color || '#6c63ff' }}>
                <div className="hero-header-content">
                  <button
                    onClick={() => setView('list')}
                    style={{
                      width: 40, height: 40, borderRadius: '50%', background: 'rgba(255,255,255,0.1)',
                      border: '1px solid rgba(255,255,255,0.2)', display: 'grid', placeItems: 'center',
                      cursor: 'pointer', color: '#fff', flexShrink: 0,
                      transition: 'all 0.2s', marginBottom: 20
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.2)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
                  >
                    <ArrowLeft size={20} />
                  </button>

                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: 24 }}>
                    <div style={{ flex: 1, minWidth: 300 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
                        <h2 style={{ margin: 0, fontSize: 36, fontWeight: 800, fontFamily: 'var(--font-display)', letterSpacing: '-0.02em', color: '#fff' }}>
                          {editing.project_name}
                        </h2>
                        {editing.project_code && (
                          <span style={{
                            background: 'var(--accent)', color: '#fff',
                            padding: '4px 12px', borderRadius: 6, fontSize: 13, fontWeight: 700,
                            letterSpacing: '0.06em', border: '1px solid rgba(255,255,255,0.3)',
                          }}>
                            {editing.project_code}
                          </span>
                        )}
                        {editing.project_key && (
                          <span style={{
                            background: 'rgba(255,255,255,0.15)', color: '#fff',
                            padding: '4px 12px', borderRadius: 6, fontSize: 13, fontWeight: 700,
                            letterSpacing: '0.06em', border: '1px solid rgba(255,255,255,0.3)',
                          }}>
                            {editing.project_key}
                          </span>
                        )}
                        <span style={{
                          background: 'rgba(255,255,255,0.2)', color: '#fff',
                          padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                        }}>
                          {editing.status}
                        </span>
                        {editing.category && (
                          <span style={{
                            background: 'rgba(255,255,255,0.1)', color: '#fff',
                            padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                            border: '1px solid rgba(255,255,255,0.3)',
                          }}>
                            📁 {editing.category}
                          </span>
                        )}
                        {editing.priority && (
                          <span style={{
                            background: 'rgba(255,255,255,0.2)', color: '#fff',
                            padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                          }}>
                            {editing.priority} Priority
                          </span>
                        )}
                      </div>
                      
                      <div style={{ display: 'flex', gap: 24, fontSize: 14, fontWeight: 500, opacity: 0.9, flexWrap: 'wrap' }}>
                        {editing.start_date && (
                          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Calendar size={16} />
                            {formatDateShort(editing.start_date)} -“ {editing.end_date ? formatDateShort(editing.end_date) : 'Ongoing'}
                          </span>
                        )}
                        {editing.team && (
                          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Users size={16} />
                            <strong style={{ fontWeight: 700 }}>{editing.team}</strong>
                          </span>
                        )}
                        {(editing.customer?.name || editing.customer?.company) && (
                          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            Customer: <strong style={{ fontWeight: 700 }}>{editing.customer.name || editing.customer.company}</strong>
                          </span>
                        )}
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: 10, flexShrink: 0, alignItems: 'center' }}>
                      {workSession.totalMinutes > 0 && !workSession.activeSession && (
                        <div style={{ color: '#fff', marginRight: 10, fontSize: 14, fontWeight: 700, opacity: 0.9 }}>
                          Time Tracked: {Math.floor(workSession.totalMinutes / 60)}h {workSession.totalMinutes % 60}m
                        </div>
                      )}
                      {workSession.activeSession && (
                        <div style={{ color: '#10b981', marginRight: 10, fontSize: 14, fontWeight: 700, background: 'rgba(16,185,129,0.15)', padding: '6px 12px', borderRadius: 8, border: '1px solid rgba(16,185,129,0.3)', display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#10b981', display: 'inline-block', animation: 'pulse 1.5s infinite' }} />
                          Session Active: {Math.floor(elapsedActiveSeconds / 3600)}h {Math.floor((elapsedActiveSeconds % 3600) / 60)}m {elapsedActiveSeconds % 60}s
                        </div>
                      )}
                      {(me?.role === 'Manager' || me?.role === 'Admin' || isAssignedToProject(editing)) && (
                        workSession.activeSession ? (
                          <button
                            onClick={handleStopWorkSession}
                            disabled={sessionLoading}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px',
                              borderRadius: 12, fontSize: 14, fontWeight: 700, cursor: 'pointer',
                              background: 'rgba(239,68,68,0.8)', border: '1px solid rgba(239,68,68,0.3)',
                              color: '#fff', transition: 'all 0.2s', backdropFilter: 'blur(10px)'
                            }}
                            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,1)'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.8)'; }}
                          >
                            <Clock size={16} /> Stop Session
                          </button>
                        ) : (
                          <button
                            onClick={handleStartWorkSession}
                            disabled={sessionLoading}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px',
                              borderRadius: 12, fontSize: 14, fontWeight: 700, cursor: 'pointer',
                              background: 'rgba(16,185,129,0.8)', border: '1px solid rgba(16,185,129,0.3)',
                              color: '#fff', transition: 'all 0.2s', backdropFilter: 'blur(10px)'
                            }}
                            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(16,185,129,1)'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(16,185,129,0.8)'; }}
                          >
                            <Clock size={16} /> Start Session
                          </button>
                        )
                      )}
                      {canUpdate && (
                        <button
                          onClick={() => openEdit(editing)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px',
                            borderRadius: 12, fontSize: 14, fontWeight: 700, cursor: 'pointer',
                            background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)',
                            color: '#fff', transition: 'all 0.2s', backdropFilter: 'blur(10px)'
                          }}
                          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.25)'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; }}
                        >
                          <Pencil size={16} /> Edit Project
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* â•â•â• TAB NAVIGATION â•â•â• */}
              <div style={{
                display: 'flex', gap: 12, marginBottom: 32,
                padding: '4px', background: 'var(--bg-hover)', borderRadius: 999, width: 'fit-content'
              }}>
                {tabs.map(tab => {
                  const isActive = detailTab === tab.key;
                  return (
                    <button
                      key={tab.key}
                      onClick={() => setDetailTab(tab.key)}
                      className={`tab-pill ${isActive ? 'active' : ''}`}
                    >
                      {tab.icon} {tab.label}
                    </button>
                  );
                })}
              </div>

              {/* â•â•â• CONTENT TABS â•â•â• */}
              
              {/* â”€â”€ OVERVIEW TAB â”€â”€ */}
              {detailTab === 'overview' && (
                <div className="animate-in" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 340px', gap: 24, minWidth: 0 }}>
                  {/* â”€â”€ LEFT COLUMN â”€â”€ */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 24, minWidth: 0 }}>

                    {/* Description Card */}
                    <div
                      className="modern-card"
                      onClick={() => editing.description && setDescriptionModal(true)}
                      style={{ cursor: editing.description ? 'pointer' : 'default' }}
                      title={editing.description ? 'Click to view the full description' : undefined}
                    >
                      {sectionHeading(<FileText size={18} />, 'Project Overview')}
                      <p style={{
                        margin: 0, fontSize: 14, color: 'var(--text-muted)', lineHeight: 1.7, fontWeight: 500,
                        whiteSpace: 'pre-wrap', overflowWrap: 'anywhere',
                        display: '-webkit-box', WebkitLineClamp: 6, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                      }}>
                        {editing.description || <span style={{ fontStyle: 'italic', color: 'var(--text-dim)' }}>No description provided.</span>}
                      </p>
                    </div>

                    {/* Overall Progress */}
                    <div className="modern-card">
                      {sectionHeading(<BarChart3 size={18} />, 'Progress Summary')}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)' }}>Completion Rate</span>
                        <span style={{ fontSize: 16, fontWeight: 800, color: 'var(--accent)' }}>{completionPct}%</span>
                      </div>
                      <div style={{
                        background: 'var(--bg-hover)', border: '1px solid var(--border-bright)',
                        width: '100%', height: 12, borderRadius: 20, overflow: 'hidden',
                      }}>
                        <div style={{
                          width: `${completionPct}%`, height: '100%', borderRadius: 20,
                          background: completionPct >= 75 ? 'var(--accent3)' : completionPct >= 40 ? 'var(--accent)' : 'var(--accent4)',
                          transition: 'width 1s cubic-bezier(0.25, 0.8, 0.25, 1)',
                          boxShadow: completionPct > 0 ? `0 0 16px ${completionPct >= 75 ? 'rgba(6,214,160,0.6)' : completionPct >= 40 ? 'rgba(108,99,255,0.6)' : 'rgba(255,209,102,0.6)'}` : 'none',
                        }} />
                      </div>
                    </div>

                    {/* Project Tasks Card */}
                    <div className="modern-card">
                      {sectionHeading(<ListTodo size={18} />, `Recent Tasks (${totalTasks})`)}
                      {/* Sprint label */}
                      {activeSprint && (
                        <div style={{
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          marginBottom: 20, padding: '12px 16px', borderRadius: 12,
                          background: 'linear-gradient(90deg, rgba(108,99,255,0.05), transparent)', border: '1px solid rgba(108,99,255,0.1)',
                        }}>
                          <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--accent)' }}>
                            <Activity size={14} style={{ display: 'inline-block', marginRight: 6, verticalAlign: 'text-bottom' }}/>
                            {activeSprint.sprint_name}
                          </span>
                          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>
                            {formatDateShort(activeSprint.start_date)} - {formatDateShort(activeSprint.end_date)}
                          </span>
                        </div>
                      )}
                      {!activeSprint && sprints.length > 0 && (
                        <div style={{
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          marginBottom: 20, padding: '12px 16px', borderRadius: 12,
                          background: 'var(--bg-hover)', border: '1px solid var(--border-bright)',
                        }}>
                          <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>
                            {sprints[sprints.length - 1].sprint_name}
                          </span>
                          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>
                            {formatDateShort(sprints[sprints.length - 1].start_date)} - {formatDateShort(sprints[sprints.length - 1].end_date)}
                          </span>
                        </div>
                      )}

                      {loadingTasks ? (
                        <div style={{ color: 'var(--text-muted)', fontSize: 13, padding: '20px 0', textAlign: 'center' }}>Loading tasks...</div>
                      ) : projectTasks.length === 0 ? (
                        <div style={{ color: 'var(--text-muted)', fontSize: 13, fontStyle: 'italic', padding: '20px 0', textAlign: 'center' }}>No tasks found for this project.</div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                          {projectTasks.slice(0, 5).map(task => (
                            <div key={task.task_id} style={{
                              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                              padding: '14px 16px', background: 'var(--bg-card)', borderRadius: 12,
                              border: '1px solid var(--border)', transition: 'all 0.2s',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
                            }}
                            onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--border-bright)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                <div style={{
                                  width: 10, height: 10, borderRadius: '50%',
                                  background: task.status === 'Done' ? 'var(--accent3)' : task.status === 'In Progress' ? '#6c63ff' : task.status === 'In Review' ? 'var(--accent4)' : 'var(--text-dim)',
                                  boxShadow: task.status === 'Done' ? '0 0 8px rgba(6,214,160,0.6)' : task.status === 'In Progress' ? '0 0 8px rgba(108,99,255,0.6)' : 'none',
                                }} />
                                <div>
                                  <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>{task.title || task.task_name}</div>
                                  <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-muted)', marginTop: 4 }}>
                                    {task.assignedUser?.name ? <span style={{ color: 'var(--text)' }}>{task.assignedUser.name}</span> : 'Unassigned'} 
                                    <span style={{ margin: '0 6px', opacity: 0.3 }}>-</span> 
                                    {task.priority}
                                  </div>
                                </div>
                              </div>
                              <span style={{
                                fontSize: 11, fontWeight: 700, padding: '4px 12px', borderRadius: 20,
                                background: task.status === 'Done' ? 'rgba(6,214,160,0.1)' : task.status === 'In Progress' ? 'rgba(108,99,255,0.1)' : 'var(--bg-hover)',
                                color: task.status === 'Done' ? 'var(--accent3)' : task.status === 'In Progress' ? '#a78bfa' : 'var(--text-muted)',
                                border: `1px solid ${task.status === 'Done' ? 'rgba(6,214,160,0.2)' : task.status === 'In Progress' ? 'rgba(108,99,255,0.2)' : 'var(--border-bright)'}`,
                              }}>
                                {task.status}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Add task button */}
                      <button
                        onClick={() => { setEditingTask(null); setTaskForm({ ...emptyTask, status: 'To Do' }); setTaskError(''); setTaskModal(true); }}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 16px',
                          fontSize: 13, fontWeight: 600, color: 'var(--accent)',
                          background: 'rgba(108,99,255,0.05)', borderRadius: 10, border: '1px solid rgba(108,99,255,0.1)', 
                          cursor: 'pointer', marginTop: 16, transition: 'all 0.2s',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(108,99,255,0.1)'; e.currentTarget.style.transform = 'scale(1.02)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'rgba(108,99,255,0.05)'; e.currentTarget.style.transform = 'scale(1)'; }}
                      >
                        <PlusCircle size={16} /> Add Task
                      </button>
                    </div>

                    {/* Team Time Tracking (Admins & Leads) */}
                    {(me?.role === 'Admin' || me?.role === 'Manager' || editing.project_lead_id === me?.user_id) && sessionSummary.length > 0 && (
                      <div className="modern-card">
                        {sectionHeading(<Clock size={18} />, 'Team Time Tracking')}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                          {sessionSummary.map(summary => (
                            <div key={summary.user?.user_id} style={{
                              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                              padding: '12px 16px', background: 'var(--bg-hover)', borderRadius: 12,
                              border: '1px solid var(--border-bright)',
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                <div style={{
                                  width: 32, height: 32, borderRadius: '50%',
                                  background: 'linear-gradient(135deg, rgba(108,99,255,0.2), rgba(108,99,255,0.05))', color: 'var(--accent)',
                                  display: 'grid', placeItems: 'center', fontSize: 11, fontWeight: 800,
                                }}>
                                  {summary.user?.name ? summary.user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : '?'}
                                </div>
                                <div>
                                  <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>{summary.user?.name || 'Unknown'}</div>
                                  <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-muted)' }}>{summary.user?.role || 'Member'}</div>
                                </div>
                              </div>
                              <div style={{ textAlign: 'right' }}>
                                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>
                                  {Math.floor(summary.totalMinutes / 60)}h {summary.totalMinutes % 60}m
                                </div>
                                {summary.activeSession && (
                                  <div style={{ fontSize: 11, fontWeight: 600, color: '#10b981', display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'flex-end', marginTop: 2 }}>
                                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#10b981', animation: 'pulse 1.5s infinite' }} />
                                    Working Now
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* â”€â”€ RIGHT COLUMN â”€â”€ */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

                    {/* Overall Activity Donut */}
                    <div className="modern-card" style={{ padding: 32 }}>
                      {sectionHeading(<Activity size={18} />, 'Activity Stream')}
                      <div style={{ position: 'relative', height: 180, display: 'flex', justifyContent: 'center', overflow: 'hidden' }}>
                        <ResponsiveContainer width="100%" height={360}>
                          <PieChart>
                            <Pie
                              data={donutData}
                              cx="50%"
                              cy="50%"
                              startAngle={180}
                              endAngle={0}
                              innerRadius={110}
                              outerRadius={140}
                              paddingAngle={totalTasks > 0 ? 3 : 0}
                              dataKey="value"
                              stroke="none"
                              cornerRadius={8}
                            />
                          </PieChart>
                        </ResponsiveContainer>
                        <div style={{ position: 'absolute', bottom: 10, left: 0, right: 0, textAlign: 'center' }}>
                          <div style={{ fontSize: 42, fontWeight: 800, color: 'var(--text-primary)', lineHeight: 1, fontFamily: 'var(--font-display)' }}>{completionPct}%</div>
                          <div style={{ fontSize: 13, color: 'var(--text-muted)', fontWeight: 600, marginTop: 4, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Completed</div>
                        </div>
                      </div>
                    </div>

                    {/* Stats Grid */}
                    <div className="modern-card">
                      {sectionHeading(<BarChart3 size={18} />, 'Metrics')}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        {[
                          { label: 'Open Tasks', value: openTasks, color: 'var(--accent)' },
                          { label: 'Done', value: doneTasks, color: 'var(--accent3)' },
                          { label: 'Milestones', value: milestones.length, color: '#a78bfa' },
                          { label: 'Blockers', value: blockerTasks, color: 'var(--accent2)' },
                        ].map((stat) => (
                          <div
                            key={stat.label}
                            style={{
                              padding: '16px 20px', borderRadius: 12,
                              background: 'var(--bg-hover)', border: '1px solid var(--border-bright)',
                              transition: 'all 0.2s', cursor: 'default'
                            }}
                            onMouseEnter={e => { e.currentTarget.style.borderColor = stat.color; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-bright)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                          >
                            <div style={{ fontSize: 28, fontWeight: 800, color: stat.color, lineHeight: 1.1, fontFamily: 'var(--font-display)' }}>{stat.value}</div>
                            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6, fontWeight: 600 }}>{stat.label}</div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Project Details */}
                    <div className="modern-card">
                      {sectionHeading(<FileText size={18} />, 'Details')}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                        {/* Project Lead */}
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>Project Lead</div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            {editing.projectLead ? (
                              <>
                                <div style={{
                                  width: 36, height: 36, borderRadius: '50%',
                                  background: 'linear-gradient(135deg, rgba(108,99,255,0.2), rgba(108,99,255,0.05))', color: 'var(--accent)',
                                  display: 'grid', placeItems: 'center', fontSize: 12, fontWeight: 800,
                                  border: '1px solid rgba(108,99,255,0.3)', boxShadow: '0 4px 10px rgba(108,99,255,0.15)'
                                }}>
                                  {editing.projectLead.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                                </div>
                                <span style={{ fontSize: 14, color: 'var(--text)', fontWeight: 700 }}>{editing.projectLead.name}</span>
                              </>
                            ) : (
                              <span style={{ fontSize: 13, color: 'var(--text-dim)', fontStyle: 'italic' }}>Unassigned</span>
                            )}
                          </div>
                        </div>

                        {/* Team Members */}
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>Team Members</div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                            {detailMemberUsers.length === 0 ? (
                              <span style={{ fontSize: 13, color: 'var(--text-dim)', fontStyle: 'italic' }}>No members</span>
                            ) : (
                              <>
                                {detailMemberUsers.slice(0, 3).map((u, idx) => (
                                  <div
                                    key={u.user_id}
                                    title={u.name}
                                    style={{
                                      width: 36, height: 36, borderRadius: '50%',
                                      background: ['var(--accent-surface)', 'rgba(255,107,107,0.15)', 'rgba(255,209,102,0.15)'][idx % 3],
                                      color: ['var(--accent)', 'var(--accent2)', 'var(--accent4)'][idx % 3],
                                      display: 'grid', placeItems: 'center', fontSize: 11, fontWeight: 800,
                                      border: '2px solid var(--bg-card)',
                                      marginLeft: idx === 0 ? 0 : -10,
                                      zIndex: 3 - idx, position: 'relative',
                                      boxShadow: '0 2px 6px rgba(0,0,0,0.1)'
                                    }}
                                  >
                                    {u.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                                  </div>
                                ))}
                                {detailMemberUsers.length > 3 && (
                                  <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginLeft: 12 }}>+{detailMemberUsers.length - 3} more</span>
                                )}
                              </>
                            )}
                          </div>
                        </div>

                        {/* Client Delivery */}
                        {(me?.role === 'Manager' || me?.role === 'Admin') && (
                          <div style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>Client Delivery</div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                              {(() => {
                                const cdUser = users.find(u => u.user_id === Number(editing.client_delivery_id));
                                return cdUser ? (
                                  <>
                                    <div style={{
                                      width: 36, height: 36, borderRadius: '50%',
                                      background: 'linear-gradient(135deg, rgba(16,185,129,0.2), rgba(16,185,129,0.05))', color: 'var(--accent3)',
                                      display: 'grid', placeItems: 'center', fontSize: 12, fontWeight: 800,
                                      border: '1px solid rgba(16,185,129,0.3)', boxShadow: '0 4px 10px rgba(16,185,129,0.15)'
                                    }}>
                                      {cdUser.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                                    </div>
                                    <span style={{ fontSize: 14, color: 'var(--text)', fontWeight: 700 }}>{cdUser.name}</span>
                                  </>
                                ) : (
                                  <span style={{ fontSize: 13, color: 'var(--text-dim)', fontStyle: 'italic' }}>Unassigned</span>
                                );
                              })()}
                            </div>
                          </div>
                        )}


                        {/* Visibility */}
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>Visibility</div>
                          <div style={{
                            display: 'inline-flex', alignItems: 'center', gap: 8,
                            fontSize: 13, fontWeight: 600, padding: '6px 14px', borderRadius: 20,
                            background: 'var(--bg-hover)', border: '1px solid var(--border-bright)',
                            color: 'var(--text)',
                          }}>
                            <Lock size={12} style={{ opacity: 0.6 }} />
                            {editing.visibility || 'Public - all members'}
                          </div>
                        </div>

                        {/* Tags */}
                        {tags.length > 0 && (
                          <div style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>Tags</div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                              {tags.map(tag => (
                                <span
                                  key={tag}
                                  style={{
                                    fontSize: 12, fontWeight: 600, padding: '4px 12px', borderRadius: 20,
                                    background: 'rgba(108,99,255,0.08)', color: 'var(--accent)',
                                    border: '1px solid rgba(108,99,255,0.15)',
                                  }}
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Client Feedback */}
                        <div style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>Client Feedback</div>
                          {editing.client_feedback ? (
                            <div style={{
                              fontSize: 13, color: 'var(--text)', lineHeight: 1.6,
                              padding: '12px 16px', borderRadius: 12,
                              background: 'rgba(108,99,255,0.05)', border: '1px solid rgba(108,99,255,0.1)',
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                                <MessageSquare size={14} style={{ color: 'var(--accent)' }} />
                                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent)' }}>Feedback</span>
                              </div>
                              {editing.client_feedback}
                            </div>
                          ) : (
                            <span style={{ fontSize: 13, color: 'var(--text-dim)', fontStyle: 'italic' }}>No feedback provided</span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Budget Card */}
                    {(me?.role === 'Admin' || me?.role === 'Client') && budgetNum > 0 && (
                      <div className="modern-card">
                        {sectionHeading(<DollarSign size={18} />, 'Budget Tracking')}
                        <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--accent3)', marginBottom: 16, lineHeight: 1, fontFamily: 'var(--font-display)' }}>
                          {currencySymbol} {budgetNum.toLocaleString()}
                        </div>
                        <div style={{
                          background: 'var(--bg-hover)', border: '1px solid var(--border-bright)',
                          width: '100%', height: 12, borderRadius: 20, overflow: 'hidden', marginBottom: 12,
                        }}>
                          <div style={{
                            width: `${budgetNum > 0 ? (budgetUsed / budgetNum) * 100 : 0}%`,
                            height: '100%', borderRadius: 20,
                            background: 'linear-gradient(90deg, var(--accent), var(--accent3))',
                            transition: 'width 1s cubic-bezier(0.25, 0.8, 0.25, 1)',
                            boxShadow: '0 0 12px rgba(108,99,255,0.5)',
                          }} />
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>
                          <span>Used: <span style={{ color: 'var(--text)' }}>${budgetUsed.toLocaleString()}</span></span>
                          <span>Remaining: <span style={{ color: 'var(--text)' }}>${budgetRemaining.toLocaleString()}</span></span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* â”€â”€ ROADMAP TAB â”€â”€ */}
              {detailTab === 'roadmap' && (() => {
                // Generate Timeline Events
                const events = [];
                
                // 1. Project Creation
                events.push({
                  id: 'project-created',
                  type: 'project',
                  title: 'Project Started',
                  description: `${editing.project_name} was created.`,
                  date: new Date(editing.createdAt || new Date()),
                  icon: <Rocket size={16} />,
                  color: 'var(--accent)',
                  bgColor: 'rgba(108, 99, 255, 0.15)',
                  borderColor: 'rgba(108, 99, 255, 0.4)'
                });

                // 2. Milestones
                milestones.forEach((m, i) => {
                  const msStatus = getMilestoneStatus(m);
                  const isDone = msStatus === 'Done';
                  events.push({
                    id: `milestone-${i}`,
                    type: 'milestone',
                    title: m.name,
                    description: m.description || `Milestone marked as ${msStatus}`,
                    date: new Date(m.date),
                    icon: <Milestone size={16} />,
                    color: isDone ? 'var(--accent3)' : '#a78bfa',
                    bgColor: isDone ? 'rgba(6, 214, 160, 0.15)' : 'rgba(167, 139, 250, 0.15)',
                    borderColor: isDone ? 'rgba(6, 214, 160, 0.4)' : 'rgba(167, 139, 250, 0.4)'
                  });
                });

                // 3. Tasks
                projectTasks.forEach(t => {
                  // Task Created
                  events.push({
                    id: `task-created-${t.task_id}`,
                    type: 'task-created',
                    title: 'Task Created',
                    description: t.task_name,
                    date: new Date(t.createdAt || new Date()),
                    icon: <FileText size={16} />,
                    color: 'var(--text-primary)',
                    bgColor: 'var(--bg-elevated)',
                    borderColor: 'var(--border-bright)',
                    meta: t.assignedUser?.name ? `Assigned to ${t.assignedUser.name}` : 'Unassigned',
                    priority: t.priority
                  });

                  // Task Completed
                  if (t.status === 'Done') {
                    events.push({
                      id: `task-done-${t.task_id}`,
                      type: 'task-done',
                      title: 'Task Completed',
                      description: t.task_name,
                      date: new Date(t.updatedAt || new Date()),
                      icon: <CheckCircle2 size={16} />,
                      color: 'var(--accent3)',
                      bgColor: 'rgba(6, 214, 160, 0.1)',
                      borderColor: 'rgba(6, 214, 160, 0.3)',
                    });
                  }
                });

                // Sort events chronologically (oldest first)
                events.sort((a, b) => a.date - b.date);

                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 24, padding: '10px 0' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                      <div style={{ padding: 8, background: 'rgba(108,99,255,0.1)', borderRadius: 8, color: 'var(--accent)' }}>
                        <Activity size={18} />
                      </div>
                      <div>
                        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: 'var(--text-primary)' }}>Project Journey</h3>
                        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>A chronological timeline of milestones, tasks, and achievements</div>
                      </div>
                    </div>

                    <div style={{ position: 'relative', paddingLeft: 40, paddingTop: 10, paddingBottom: 20 }}>
                      {/* Vertical Glowing Line */}
                      <div style={{
                        position: 'absolute',
                        top: 0, bottom: 0, left: 15,
                        width: 2,
                        background: 'linear-gradient(to bottom, var(--accent), var(--accent3), transparent)',
                        boxShadow: '0 0 10px rgba(108, 99, 255, 0.4)',
                        borderRadius: 2
                      }} />

                      {events.map((ev, index) => (
                        <div key={ev.id} style={{
                          position: 'relative',
                          marginBottom: index === events.length - 1 ? 0 : 32,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 8,
                          animation: `slideInUp ${0.3 + index * 0.05}s ease-out both`
                        }}>
                          {/* Timeline Node */}
                          <div style={{
                            position: 'absolute',
                            left: -41, // 40 padding - 15 line pos + centering
                            top: 4,
                            width: 32, height: 32,
                            borderRadius: '50%',
                            background: ev.bgColor,
                            border: `2px solid ${ev.borderColor}`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: ev.color,
                            boxShadow: `0 0 12px ${ev.bgColor}`,
                            zIndex: 2
                          }}>
                            {ev.icon}
                          </div>

                          {/* Date Label */}
                          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', letterSpacing: '0.05em' }}>
                            {ev.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </div>

                          {/* Event Card */}
                          <div style={{
                            background: 'var(--bg-card)',
                            border: '1px solid var(--border)',
                            borderRadius: 12,
                            padding: '16px 20px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                            transition: 'transform 0.2s, box-shadow 0.2s, border-color 0.2s',
                            cursor: 'default',
                            position: 'relative',
                            overflow: 'hidden'
                          }}
                          onMouseEnter={e => {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.1)';
                            e.currentTarget.style.borderColor = ev.borderColor;
                          }}
                          onMouseLeave={e => {
                            e.currentTarget.style.transform = 'none';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.05)';
                            e.currentTarget.style.borderColor = 'var(--border)';
                          }}
                          >
                            {/* Accent Bar */}
                            <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, background: ev.color }} />
                            
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
                              <div>
                                <h4 style={{ margin: '0 0 6px 0', fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>
                                  {ev.title}
                                </h4>
                                <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                                  {ev.description}
                                </p>
                              </div>
                              
                              {(ev.meta || ev.priority) && (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
                                  {ev.priority && (
                                    <Badge value={ev.priority} />
                                  )}
                                  {ev.meta && (
                                    <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-dim)', background: 'var(--bg-hover)', padding: '4px 8px', borderRadius: 6 }}>
                                      {ev.meta}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                      
                      {events.length === 0 && (
                        <div style={{ color: 'var(--text-muted)', fontStyle: 'italic', fontSize: 13 }}>No activity recorded yet.</div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* â”€â”€ MILESTONES TAB â”€â”€ */}
              {detailTab === 'milestones' && (
                <div className="animate-in" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                  {milestones.length === 0 ? (
                    <div className="modern-card" style={{ padding: '60px 20px', textAlign: 'center' }}>
                      <div style={{ color: 'var(--text-muted)', fontSize: 14, fontStyle: 'italic' }}>No milestones defined for this project.</div>
                    </div>
                  ) : milestones.map((m, idx) => {
                    const msStatus = getMilestoneStatus(m);
                    const pct = msStatus === 'Done' ? 100 : msStatus === 'In Progress' ? 50 : 0;
                    const icon = msStatus === 'Done' ? <CheckCircle2 size={24} /> : msStatus === 'In Progress' ? <Pencil size={24} /> : <Lock size={24} />;

                    return (
                      <div key={idx} className="modern-card" style={{ padding: '24px 32px', display: 'flex', flexDirection: 'column', gap: 20 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 24, minWidth: 0 }}>
                          <div style={{ display: 'flex', gap: 20, flex: 1, minWidth: 0 }}>
                            <div style={{
                              width: 54, height: 54, borderRadius: 16,
                              background: msStatus === 'Done' ? 'rgba(6,214,160,0.1)' : msStatus === 'In Progress' ? 'rgba(108,99,255,0.1)' : 'var(--bg-hover)',
                              color: msStatus === 'Done' ? 'var(--accent3)' : msStatus === 'In Progress' ? 'var(--accent)' : 'var(--text-dim)',
                              border: `1px solid ${msStatus === 'Done' ? 'rgba(6,214,160,0.2)' : msStatus === 'In Progress' ? 'rgba(108,99,255,0.2)' : 'var(--border-bright)'}`,
                              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                              boxShadow: msStatus === 'Done' ? '0 4px 12px rgba(6,214,160,0.15)' : msStatus === 'In Progress' ? '0 4px 12px rgba(108,99,255,0.15)' : 'none'
                            }}>
                              {icon}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 6 }}>{m.name}</div>
                              <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{m.description || 'Milestone'} <span style={{ opacity: 0.3, margin: '0 8px' }}>-</span> {formatDateShort(m.date)}</div>
                            </div>
                          </div>
                          <div style={{ width: 100, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 10 }}>
                            <span style={{
                              fontSize: 12, fontWeight: 700, padding: '6px 14px', borderRadius: 20,
                              background: msStatus === 'Done' ? 'rgba(6,214,160,0.12)' : msStatus === 'In Progress' ? 'rgba(108,99,255,0.12)' : 'var(--bg-elevated)',
                              color: msStatus === 'Done' ? 'var(--accent3)' : msStatus === 'In Progress' ? '#a78bfa' : 'var(--text-muted)',
                              border: `1px solid ${msStatus === 'Done' ? 'rgba(6,214,160,0.2)' : msStatus === 'In Progress' ? 'rgba(108,99,255,0.2)' : 'var(--border-bright)'}`,
                              whiteSpace: 'nowrap',
                            }}>
                              {msStatus}
                            </span>
                            <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--text-primary)', whiteSpace: 'nowrap', fontFamily: 'var(--font-display)' }}>{pct}%</div>
                            {msStatus === 'Done' && !m.approved && !m.manager_approved && ['Admin', 'Manager'].includes(user?.role) && (
                              <button
                                type="button"
                                onClick={() => handleApproveMilestone(idx)}
                                style={{
                                  border: '1px solid rgba(6,214,160,0.35)', borderRadius: 8,
                                  background: 'rgba(6,214,160,0.1)', color: 'var(--accent3)',
                                  padding: '6px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                Approve for client
                              </button>
                            )}
                          </div>
                        </div>
                        <div style={{
                          background: 'var(--bg-hover)', border: '1px solid var(--border-bright)',
                          width: '100%', height: 10, borderRadius: 20, overflow: 'hidden',
                        }}>
                          <div style={{
                            width: `${pct}%`, height: '100%', borderRadius: 20,
                            background: msStatus === 'Done' ? 'var(--accent3)' : 'var(--accent)',
                            boxShadow: msStatus === 'Done' ? '0 0 16px rgba(6,214,160,0.6)' : msStatus === 'In Progress' ? '0 0 16px rgba(108,99,255,0.6)' : 'none',
                            transition: 'width 1s cubic-bezier(0.25, 0.8, 0.25, 1)'
                          }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* â”€â”€ TASKS TAB â”€â”€ */}
              {detailTab === 'tasks' && (
                <div className="animate-in" style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                  <div className="modern-card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{ width: 40, height: 40, borderRadius: 12, background: 'rgba(108,99,255,0.1)', color: 'var(--accent)', display: 'grid', placeItems: 'center', border: '1px solid rgba(108,99,255,0.2)' }}>
                        <FileText size={20} />
                      </div>
                      <div>
                        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: 'var(--text-primary)' }}>Project Tasks</h3>
                        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>Manage and track all tasks for this project</div>
                      </div>
                    </div>
                    <button 
                      onClick={() => { setEditingTask(null); setTaskForm({ ...emptyTask, status: 'To Do' }); setTaskError(''); setTaskModal(true); }}
                      style={{
                      display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px',
                      borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer',
                      background: 'var(--accent)', border: 'none', color: '#fff', 
                      boxShadow: '0 4px 14px rgba(108,99,255,0.4)', transition: 'all 0.2s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
                    onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
                    >
                      <Plus size={16} /> New Task
                    </button>
                  </div>

                  {projectTasks.length === 0 && (
                    <div className="modern-card" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20, padding: '60px 0' }}>
                      <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'var(--bg-hover)', color: 'var(--text-dim)', display: 'grid', placeItems: 'center' }}><ListTodo size={32} /></div>
                      <div style={{ fontSize: 15, color: 'var(--text-muted)', fontWeight: 500 }}>No tasks found for this project. Add your first task to get started.</div>
                      <button style={{
                        display: 'flex', alignItems: 'center', gap: 8, padding: '12px 24px',
                        borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer',
                        background: 'rgba(108,99,255,0.05)', border: '1px dashed rgba(108,99,255,0.4)',
                        color: 'var(--accent)', transition: 'all 0.2s',
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(108,99,255,0.1)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'rgba(108,99,255,0.05)'}
                      >
                        <Plus size={18} /> Generate tasks from description
                      </button>
                    </div>
                  )}

                  {projectTasks.length > 0 && (
                    <div className="modern-card" style={{ padding: 0, overflow: 'hidden' }}>
                      <div style={{ padding: '20px 24px', background: 'var(--bg-hover)', borderBottom: '1px solid var(--border)' }}>
                        <h4 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 10 }}>
                          <ListTodo size={18} style={{ color: 'var(--accent)' }} /> All Tasks
                        </h4>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        {projectTasks.map((t, idx) => {
                          const priorityColor = t.priority === 'Critical' ? '#f87171' : t.priority === 'High' ? '#fbbf24' : t.priority === 'Medium' ? '#60a5fa' : 'var(--text-dim)';
                          return (
                            <div key={t.task_id} style={{
                              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                              padding: '16px 24px', borderBottom: idx < projectTasks.length - 1 ? '1px solid var(--border)' : 'none',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = 'rgba(108,99,255,0.02)'}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                                <div style={{ 
                                  width: 20, height: 20, borderRadius: 6, border: `2px solid ${t.status === 'Done' ? 'var(--accent3)' : 'var(--border-bright)'}`, 
                                  background: t.status === 'Done' ? 'var(--accent3)' : 'transparent',
                                  cursor: 'pointer', display: 'grid', placeItems: 'center'
                                }}>
                                  {t.status === 'Done' && <CheckCircle2 size={12} color="#fff" />}
                                </div>
                                <div>
                                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', textDecoration: t.status === 'Done' ? 'line-through' : 'none', opacity: t.status === 'Done' ? 0.6 : 1 }}>
                                    {t.task_name || t.title}
                                  </div>
                                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <span style={{
                                      fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 12,
                                      background: 'var(--bg-elevated)', color: 'var(--text-dim)', border: '1px solid var(--border-bright)'
                                    }}>{t.status}</span>
                                    {t.assignedUser?.name && <span>Assignee: {t.assignedUser.name}</span>}
                                  </div>
                                </div>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'var(--bg-hover)', padding: '4px 10px', borderRadius: 20, border: '1px solid var(--border-bright)' }}>
                                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: priorityColor }} title={`Priority: ${t.priority}`} />
                                  <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)' }}>{t.priority}</span>
                                </div>

                                <div style={{ display: 'flex', gap: 8, marginLeft: 8 }}>
                                  <button onClick={(e) => { e.stopPropagation(); setEditingTask(t); setTaskForm({ task_name: t.task_name, description: t.description||'', status: t.status, priority: t.priority, project_id: t.project_id, assigned_to: t.assigned_to||'' }); setTaskError(''); setTaskModal(true); }} 
                                    style={{ background: 'var(--bg-card)', border: '1px solid var(--border-bright)', borderRadius: 8, color: 'var(--text-muted)', cursor: 'pointer', padding: 8, transition: 'all 0.2s' }} 
                                    onMouseEnter={e => { e.currentTarget.style.color = 'var(--accent)'; e.currentTarget.style.borderColor = 'var(--accent)'; }}
                                    onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-muted)'; e.currentTarget.style.borderColor = 'var(--border-bright)'; }}
                                    title="Edit Task">
                                    <Pencil size={14} />
                                  </button>
                                  <button onClick={(e) => { e.stopPropagation(); handleTaskDelete(t.task_id); }} 
                                    style={{ background: 'var(--bg-card)', border: '1px solid var(--border-bright)', borderRadius: 8, color: 'var(--text-muted)', cursor: 'pointer', padding: 8, transition: 'all 0.2s' }} 
                                    onMouseEnter={e => { e.currentTarget.style.color = '#f87171'; e.currentTarget.style.borderColor = '#f87171'; }}
                                    onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-muted)'; e.currentTarget.style.borderColor = 'var(--border-bright)'; }}
                                    title="Delete Task">
                                    <Trash2 size={14} />
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* â”€â”€ ACTIVITY TAB â”€â”€ */}
              {detailTab === 'activity' && (
                <div className="animate-in modern-card">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 32 }}>
                    <div style={{ width: 40, height: 40, borderRadius: 12, background: 'rgba(108,99,255,0.1)', color: 'var(--accent)', display: 'grid', placeItems: 'center', border: '1px solid rgba(108,99,255,0.2)' }}>
                      <Activity size={20} />
                    </div>
                    <div>
                      <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: 'var(--text-primary)' }}>Activity Timeline</h3>
                      <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>Recent changes and updates for this project</div>
                    </div>
                  </div>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', position: 'relative', paddingLeft: 12 }}>
                    {/* Vertical Timeline Line */}
                    <div style={{ position: 'absolute', left: 23, top: 20, bottom: 20, width: 2, background: 'linear-gradient(to bottom, var(--accent) 0%, var(--border) 20%)', borderRadius: 2 }} />
                    
                    {(() => {
                      const acts = [];
                      if (editing) {
                        acts.push({
                          user: editing.projectLead?.name || 'System',
                          action: 'created the project',
                          target: editing.project_name,
                          date: formatDateShort(editing.createdAt || new Date()),
                          color: 'var(--accent)'
                        });
                        milestones.forEach(m => {
                          acts.push({
                            user: 'System',
                            action: 'added milestone',
                            target: m.name,
                            date: formatDateShort(m.date),
                            color: '#a78bfa'
                          });
                        });
                        if ((me?.role === 'Admin' || me?.role === 'Client') && editing.estimated_budget) {
                          acts.push({
                            user: 'System',
                            action: 'set budget to',
                            target: `${editing.currency ? editing.currency.split(' ')[0] : '$'} ${Number(editing.estimated_budget).toLocaleString()}`,
                            date: formatDateShort(editing.createdAt || new Date()),
                            color: '#fbbf24'
                          });
                        }
                      }
                      
                      return acts.map((act, idx) => (
                        <div key={idx} style={{ display: 'flex', gap: 24, padding: '20px 0', position: 'relative', zIndex: 1 }}>
                          <div style={{ padding: '6px 0 0 0', flexShrink: 0 }}>
                            <div style={{ 
                              width: 14, height: 14, borderRadius: '50%', background: act.color, 
                              border: '3px solid var(--bg-card)', boxShadow: `0 0 0 2px ${act.color}40`,
                              position: 'relative', zIndex: 2
                            }} />
                          </div>
                          <div style={{ 
                            minWidth: 0, background: 'var(--bg-hover)', padding: '16px 20px', 
                            borderRadius: 12, border: '1px solid var(--border)', flex: 1 
                          }}>
                            <div style={{ fontSize: 14, color: 'var(--text)', marginBottom: 6 }}>
                              <strong style={{ fontWeight: 700 }}>{act.user}</strong> <span style={{ color: 'var(--text-muted)' }}>{act.action}</span> {act.target && <strong style={{ fontWeight: 700 }}>{act.target}</strong>}
                            </div>
                            <div style={{ fontSize: 12, color: 'var(--text-dim)', fontWeight: 600 }}>{act.date}</div>
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                </div>
              )}

               {/* â”€â”€ POC CHECKLIST TAB â”€â”€ */}
              {detailTab === 'poc' && (
                <div className="animate-in" style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>

                  {/* HEADER */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                      <div style={{ width: 48, height: 48, borderRadius: 16, background: 'linear-gradient(135deg, #6c63ff22, #a78bfa22)', color: 'var(--accent)', display: 'grid', placeItems: 'center', border: '1px solid rgba(108,99,255,0.25)', boxShadow: '0 0 20px rgba(108,99,255,0.15)' }}>
                        <CheckSquare size={24} />
                      </div>
                      <div>
                        <h3 style={{ margin: 0, fontSize: 22, fontWeight: 900, color: 'var(--text-primary)', letterSpacing: '-0.02em' }}>POC Validation Dashboard</h3>
                        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 3 }}>Proof of Concept checklist - 15 categories, all phases tracked</div>
                      </div>
                    </div>
                    {/* Auto-save badge */}
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700,
                      padding: '8px 16px', borderRadius: 20, transition: 'all 0.3s',
                      color: pocSaving === 'saving' ? '#818cf8' : pocSaving === 'saved' ? 'var(--accent3)' : pocSaving === 'error' ? '#f87171' : 'var(--text-dim)',
                      background: pocSaving === 'saving' ? 'rgba(129,140,248,0.08)' : pocSaving === 'saved' ? 'rgba(6,214,160,0.08)' : pocSaving === 'error' ? 'rgba(248,113,113,0.08)' : 'var(--bg-card)',
                      border: `1px solid ${pocSaving === 'saving' ? 'rgba(129,140,248,0.2)' : pocSaving === 'saved' ? 'rgba(6,214,160,0.2)' : pocSaving === 'error' ? 'rgba(248,113,113,0.2)' : 'var(--border)'}`
                    }}>
                      {pocSaving === 'saving' && <><div style={{ width: 8, height: 8, borderRadius: '50%', background: '#818cf8', animation: 'pulse 1s infinite' }} /> Saving-¦</>}
                      {pocSaving === 'saved' && <><CheckCircle2 size={14} /> Auto-saved</>}
                      {pocSaving === 'error' && <>âš ï¸ Save failed</>}
                      {!pocSaving && <>âš¡ Auto-saves on change</>}
                    </div>
                  </div>

                  {(() => {
                    let currentChecks = editing.poc_checklist;
                    if (typeof currentChecks === 'string') {
                      try { currentChecks = JSON.parse(currentChecks); } catch { currentChecks = {}; }
                    }
                    currentChecks = currentChecks || {};
                    const totalItems = POC_SECTIONS.reduce((sum, sec) => sum + sec.items.length, 0);
                    const totalChecked = POC_SECTIONS.reduce((sum, sec) => sum + (currentChecks[sec.id]?.length || 0), 0);
                    const overallPct = totalItems === 0 ? 0 : Math.round((totalChecked / totalItems) * 100);

                    // SVG ring helper
                    const RING_R = 38;
                    const RING_CIRC = 2 * Math.PI * RING_R;
                    const makeRing = (pct) => RING_CIRC - (pct / 100) * RING_CIRC;

                    const PHASE_COLORS = ['#6c63ff', '#06b6d4', '#f97316', '#ec4899', '#10b981', '#a78bfa'];

                    return (
                      <>
                        {/* â”€â”€ MISSION CONTROL: LARGE RING + 6 PHASE RINGS â”€â”€ */}
                        <div className="modern-card" style={{
                          padding: '32px',
                          background: 'linear-gradient(135deg, rgba(108,99,255,0.03) 0%, rgba(167,139,250,0.05) 100%)',
                          border: '1px solid rgba(108,99,255,0.12)'
                        }}>
                          <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 40, alignItems: 'center' }}>

                            {/* Big centre ring */}
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                              <div style={{ position: 'relative', width: 140, height: 140 }}>
                                <svg width={140} height={140} style={{ transform: 'rotate(-90deg)' }}>
                                  <circle cx={70} cy={70} r={RING_R + 18} fill="none" stroke="rgba(108,99,255,0.08)" strokeWidth={14} />
                                  <circle cx={70} cy={70} r={RING_R + 18} fill="none"
                                    stroke={overallPct === 100 ? 'var(--accent3)' : 'url(#mainGrad)'}
                                    strokeWidth={14} strokeLinecap="round"
                                    strokeDasharray={2 * Math.PI * (RING_R + 18)}
                                    strokeDashoffset={(2 * Math.PI * (RING_R + 18)) - (overallPct / 100) * (2 * Math.PI * (RING_R + 18))}
                                    style={{ transition: 'stroke-dashoffset 1.2s cubic-bezier(0.25,0.8,0.25,1)' }}
                                  />
                                  <defs>
                                    <linearGradient id="mainGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                                      <stop offset="0%" stopColor="#6c63ff" />
                                      <stop offset="100%" stopColor="#a78bfa" />
                                    </linearGradient>
                                  </defs>
                                </svg>
                                <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                  <div style={{ fontSize: 28, fontWeight: 900, color: overallPct === 100 ? 'var(--accent3)' : 'var(--accent)', fontFamily: 'var(--font-display)', lineHeight: 1 }}>{overallPct}%</div>
                                  <div style={{ fontSize: 10, color: 'var(--text-dim)', fontWeight: 600, marginTop: 2 }}>OVERALL</div>
                                </div>
                              </div>
                              <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--text-primary)' }}>{totalChecked} / {totalItems}</div>
                                <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>items validated</div>
                              </div>
                            </div>

                            {/* 6 phase rings grid */}
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20 }}>
                              {POC_PHASES.map((phase, phIdx) => {
                                const phSections = POC_SECTIONS.filter(s => phase.sectionIds.includes(s.id));
                                const phTotal = phSections.reduce((sum, s) => sum + s.items.length, 0);
                                const phChecked = phSections.reduce((sum, s) => sum + (currentChecks[s.id]?.length || 0), 0);
                                const phPct = phTotal === 0 ? 0 : Math.round((phChecked / phTotal) * 100);
                                const color = PHASE_COLORS[phIdx];
                                const isExpanded = pocStep === phIdx;

                                return (
                                  <div key={phIdx}
                                    onClick={() => setPocStep(isExpanded ? -1 : phIdx)}
                                    style={{
                                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
                                      padding: '16px 12px', borderRadius: 16, cursor: 'pointer',
                                      background: isExpanded ? `${color}12` : 'rgba(255,255,255,0.02)',
                                      border: `1px solid ${isExpanded ? `${color}40` : 'var(--border)'}`,
                                      transition: 'all 0.25s'
                                    }}
                                    onMouseEnter={e => { if (!isExpanded) e.currentTarget.style.background = `${color}08`; }}
                                    onMouseLeave={e => { if (!isExpanded) e.currentTarget.style.background = 'rgba(255,255,255,0.02)'; }}
                                  >
                                    <div style={{ position: 'relative', width: 72, height: 72 }}>
                                      <svg width={72} height={72} style={{ transform: 'rotate(-90deg)' }}>
                                        <circle cx={36} cy={36} r={RING_R} fill="none" stroke={`${color}20`} strokeWidth={8} />
                                        <circle cx={36} cy={36} r={RING_R} fill="none"
                                          stroke={phPct === 100 ? 'var(--accent3)' : color}
                                          strokeWidth={8} strokeLinecap="round"
                                          strokeDasharray={RING_CIRC}
                                          strokeDashoffset={makeRing(phPct)}
                                          style={{ transition: 'stroke-dashoffset 1s ease' }}
                                        />
                                      </svg>
                                      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                        {phPct === 100
                                          ? <CheckCircle2 size={20} color="var(--accent3)" />
                                          : <span style={{ fontSize: 15, fontWeight: 900, color, lineHeight: 1 }}>{phPct}%</span>
                                        }
                                      </div>
                                    </div>
                                    <div style={{ textAlign: 'center' }}>
                                      <div style={{ fontSize: 11, fontWeight: 800, color: isExpanded ? color : 'var(--text)', lineHeight: 1.3 }}>{phase.name}</div>
                                      <div style={{ fontSize: 10, color: 'var(--text-dim)', fontWeight: 600, marginTop: 2 }}>{phChecked}/{phTotal}</div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>

                        {/* â”€â”€ AUDIT SCORECARD: EXPANDABLE ROWS â”€â”€ */}
                        <div className="modern-card" style={{ padding: 0, overflow: 'hidden' }}>
                          {/* Table header */}
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 120px 40px', gap: 12, padding: '14px 24px', borderBottom: '1px solid var(--border)', background: 'var(--bg-hover)' }}>
                            <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Category</div>
                            <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', textAlign: 'center' }}>Status</div>
                            <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Progress</div>
                            <div></div>
                          </div>

                          {POC_SECTIONS.map((section, sIdx) => {
                            const checkedCount = currentChecks[section.id]?.length || 0;
                            const total = section.items.length;
                            const pct = Math.round((checkedCount / total) * 100);
                            const isExpanded = pocStep === sIdx + 100; // offset to not clash with phase rings

                            const ragColor = pct === 0 ? '#f87171' : pct === 100 ? 'var(--accent3)' : '#fbbf24';
                            const ragLabel = pct === 0 ? 'Not Started' : pct === 100 ? 'Complete' : 'In Progress';

                            return (
                              <div key={section.id} style={{ borderBottom: '1px solid var(--border)' }}>
                                {/* Row */}
                                <div
                                  onClick={() => setPocStep(isExpanded ? -1 : sIdx + 100)}
                                  style={{ display: 'grid', gridTemplateColumns: '1fr 80px 120px 40px', gap: 12, padding: '16px 24px', cursor: 'pointer', alignItems: 'center', transition: 'background 0.15s', background: isExpanded ? 'rgba(108,99,255,0.04)' : 'transparent' }}
                                  onMouseEnter={e => { if (!isExpanded) e.currentTarget.style.background = 'var(--bg-hover)'; }}
                                  onMouseLeave={e => { if (!isExpanded) e.currentTarget.style.background = 'transparent'; }}
                                >
                                  {/* Name */}
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <div style={{ width: 28, height: 28, borderRadius: 8, background: `${ragColor}18`, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                                      <span style={{ fontSize: 11, fontWeight: 900, color: ragColor }}>{section.id}</span>
                                    </div>
                                    <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{section.title.replace(/^\d+\.\s*/, '')}</span>
                                  </div>

                                  {/* RAG dot */}
                                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '4px 10px', borderRadius: 20, background: `${ragColor}15`, border: `1px solid ${ragColor}35` }}>
                                      <div style={{ width: 7, height: 7, borderRadius: '50%', background: ragColor, boxShadow: `0 0 6px ${ragColor}` }} />
                                      <span style={{ fontSize: 10, fontWeight: 800, color: ragColor }}>{pct === 0 ? 'NEW' : pct === 100 ? 'DONE' : 'WIP'}</span>
                                    </div>
                                  </div>

                                  {/* Progress bar */}
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <div style={{ flex: 1, height: 6, borderRadius: 20, background: 'var(--bg-elevated)', overflow: 'hidden' }}>
                                      <div style={{ width: `${pct}%`, height: '100%', borderRadius: 20, background: pct === 100 ? 'var(--accent3)' : `linear-gradient(90deg, ${ragColor}, ${ragColor}aa)`, transition: 'width 0.8s ease', boxShadow: pct > 0 ? `0 0 8px ${ragColor}60` : 'none' }} />
                                    </div>
                                    <span style={{ fontSize: 11, fontWeight: 800, color: 'var(--text-muted)', minWidth: 30, textAlign: 'right' }}>{checkedCount}/{total}</span>
                                  </div>

                                  {/* Expand chevron */}
                                  <div style={{ display: 'flex', justifyContent: 'center', color: 'var(--text-dim)', transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
                                    <ChevronRight size={16} style={{ transform: 'rotate(90deg)' }} />
                                  </div>
                                </div>

                                {/* Expanded items */}
                                {isExpanded && (
                                  <div className="animate-in" style={{ padding: '0 24px 20px 24px', display: 'flex', flexDirection: 'column', gap: 0 }}>
                                    <div style={{ paddingLeft: 38, display: 'flex', flexDirection: 'column' }}>
                                      {section.items.map((item, iIdx) => {
                                        const isChecked = (currentChecks[section.id] || []).includes(item);
                                        return (
                                          <label key={item} style={{
                                            display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer',
                                            padding: '10px 12px', borderRadius: 10, margin: '2px 0',
                                            background: isChecked ? 'rgba(6,214,160,0.04)' : 'transparent',
                                            border: `1px solid ${isChecked ? 'rgba(6,214,160,0.12)' : 'transparent'}`,
                                            transition: 'all 0.15s'
                                          }}
                                            onMouseEnter={e => { if (!isChecked) e.currentTarget.style.background = 'var(--bg-hover)'; }}
                                            onMouseLeave={e => { if (!isChecked) e.currentTarget.style.background = 'transparent'; }}
                                          >
                                            <div style={{
                                              width: 22, height: 22, borderRadius: 7, flexShrink: 0, display: 'grid', placeItems: 'center',
                                              border: `2px solid ${isChecked ? 'var(--accent3)' : 'var(--border-bright)'}`,
                                              background: isChecked ? 'var(--accent3)' : 'var(--bg-card)',
                                              transition: 'all 0.2s', boxShadow: isChecked ? '0 0 10px rgba(6,214,160,0.5)' : 'none'
                                            }}>
                                              {isChecked && <CheckSquare size={13} color="#fff" />}
                                            </div>
                                            <span style={{ fontSize: 14, fontWeight: 500, color: isChecked ? 'var(--text-muted)' : 'var(--text)', textDecoration: isChecked ? 'line-through' : 'none', flex: 1, lineHeight: 1.4 }}>
                                              {item}
                                            </span>
                                            {isChecked && <CheckCircle2 size={14} color="var(--accent3)" style={{ flexShrink: 0 }} />}
                                            <input type="checkbox" checked={isChecked} onChange={() => handlePOCToggle(section.id, item)} style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }} />
                                          </label>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>

                        {/* Common Deliverables Banner */}
                        <div className="modern-card" style={{ background: 'linear-gradient(135deg, rgba(108,99,255,0.04), rgba(167,139,250,0.06))', border: '1px solid rgba(108,99,255,0.15)' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                            <div style={{ width: 32, height: 32, borderRadius: 10, background: 'rgba(108,99,255,0.1)', display: 'grid', placeItems: 'center', color: 'var(--accent)' }}>
                              <CheckSquare size={16} />
                            </div>
                            <h4 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: 'var(--accent)' }}>Common POC Deliverables for IT Products</h4>
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 10 }}>
                            {['Working web/mobile application prototype', 'Database schema and sample data', 'Authentication and authorization', 'Core business workflow', 'REST/GraphQL APIs', 'Basic dashboard', 'Deployment in a test environment', 'Technical documentation', 'Architecture diagrams', 'Demo presentation'].map((del, i) => (
                              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 8, background: 'rgba(255,255,255,0.02)' }}>
                                <div style={{ width: 20, height: 20, borderRadius: 6, background: 'rgba(108,99,255,0.1)', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                                  <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--accent)' }} />
                                </div>
                                <span style={{ fontSize: 13, color: 'var(--text)', fontWeight: 500 }}>{del}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </>
                    );
                  })()}
                </div>
              )}

            </div>
          );
        })()
      ) : (
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '8px 0' }}>
          {/* Breadcrumbs */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            fontSize: 12, color: 'var(--text-muted)', marginBottom: 8, fontWeight: 500
          }}>
            <span style={{ cursor: 'pointer' }} onClick={() => setView('list')}>Projects</span>
            <ChevronRight size={12} style={{ color: 'var(--text-dim)' }} />
            <span style={{ color: 'var(--text)' }}>{view === 'create' ? 'New project' : 'Edit project'}</span>
          </div>

          {/* Form Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
            <h1 style={{
              fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 800,
              color: 'var(--text)', letterSpacing: '-0.02em'
            }}>
              {view === 'create' ? 'Create a new project' : 'Edit project'}
            </h1>
          </div>

          <Alert type="error" message={error} />

          <form onSubmit={handleSave} style={{ display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            {/* â•â•â•â•â•â•â• Left Column â•â•â•â•â•â•â• */}
            <div style={{ flex: 1, minWidth: 0 }}>

              {/* BASIC INFO */}
              <div className="modern-card" style={{ padding: '32px', marginBottom: '24px' }}>
                <h3 style={{ fontSize: 18, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 24, display: 'flex', alignItems: 'center', gap: 10 }}><FileText size={18} style={{ color: 'var(--accent)' }}/> Basic Info</h3>

                <div style={grid2}>
                  <FormField label="Project name *">
                    <input
                      type="text"
                      required
                      placeholder="e.g. Mobile app redesign"
                      value={form.project_name}
                      onChange={e => setForm(f => ({ ...f, project_name: e.target.value }))}
                    />
                  </FormField>

                  <FormField label="Project code *">
                    <input
                      type="text"
                      required
                      placeholder="e.g. PRJ-2026"
                      value={form.project_code}
                      onChange={e => setForm(f => ({ ...f, project_code: e.target.value }))}
                    />
                  </FormField>
                </div>

                <FormField label="Description">
                  <textarea
                    rows={4}
                    placeholder="What is this project about? Add goals, scope, or context..."
                    value={form.description}
                    onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                    style={{ resize: 'vertical' }}
                  />
                </FormField>

                <div style={grid2}>
                  <FormField label="Project key *">
                    <input
                      type="text"
                      required
                      placeholder="F.G. HAR"
                      value={form.project_key}
                      onChange={e => setForm(f => ({ ...f, project_key: e.target.value.toUpperCase() }))}
                    />
                    <span style={{ fontSize: 10, color: 'var(--text-dim)', marginTop: 4, display: 'block' }}>
                      Used as prefix for task IDs (KEY-101)
                    </span>
                  </FormField>

                  <FormField label="Category">
                    <input
                      type="text"
                      list="project-categories"
                      placeholder="e.g. AI Project, Internal"
                      value={form.category}
                      onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                    />
                    <datalist id="project-categories">
                      <option value="Regular Project" />
                      <option value="AI Project" />
                      <option value="Internal Project" />
                      <option value="Client Project" />
                      <option value="Maintenance" />
                    </datalist>
                  </FormField>

                  <FormField label="Team / department *">
                    <select
                      required
                      value={form.team}
                      onChange={e => {
                        const selectedTeamName = e.target.value;
                        const selectedTeamObj = teams.find(t => t.name === selectedTeamName);
                        setForm(f => {
                          const updated = { ...f, team: selectedTeamName };
                          if (selectedTeamObj) {
                            if (selectedTeamObj.team_lead_id) {
                              updated.project_lead_id = String(selectedTeamObj.team_lead_id);
                            }
                            if (selectedTeamObj.members && selectedTeamObj.members.length > 0) {
                              updated.members = selectedTeamObj.members.map(m => m.user_id);
                            }
                          }
                          return updated;
                        });
                      }}
                    >
                      <option value="">Select a team...</option>
                      {teams.map(t => (
                        <option key={t.name} value={t.name}>{t.name}</option>
                      ))}
                    </select>
                  </FormField>
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label style={{
                    display: 'block', marginBottom: 8, fontSize: 12.5, fontWeight: 500,
                    color: 'var(--text-muted)', letterSpacing: '0.04em', textTransform: 'uppercase'
                  }}>Project color</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                    {colorOptions.map(color => {
                      const isSelected = form.project_color === color;
                      return (
                        <button
                          key={color}
                          type="button"
                          onClick={() => setForm(f => ({ ...f, project_color: color }))}
                          style={{
                            width: 28, height: 28, borderRadius: '50%', backgroundColor: color,
                            border: isSelected ? '2px solid var(--text)' : '2px solid transparent',
                            cursor: 'pointer', transition: 'all 0.15s', position: 'relative',
                            transform: isSelected ? 'scale(1.12)' : 'scale(1)',
                            boxShadow: isSelected ? `0 0 10px ${color}60` : 'none',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            opacity: isSelected ? 1 : 0.8,
                          }}
                        >
                          {isSelected && (
                            <div style={{
                              width: 6, height: 6, borderRadius: '50%', background: '#fff'
                            }} />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* TIMELINE & MILESTONES */}
              <div className="modern-card" style={{ padding: '32px', marginBottom: '24px' }}>
                <h3 style={{ fontSize: 18, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 24, display: 'flex', alignItems: 'center', gap: 10 }}><Calendar size={18} style={{ color: 'var(--accent)' }}/> Timeline & Milestones</h3>

                <div style={grid2}>
                  <FormField label="Start date *">
                    <input
                      type="date"
                      required
                      value={form.start_date}
                      onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))}
                    />
                  </FormField>

                  <FormField label="Target end date *">
                    <input
                      type="date"
                      required
                      value={form.end_date}
                      onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))}
                    />
                  </FormField>
                </div>

                <div>
                  <label style={{
                    display: 'block', marginBottom: 8, fontSize: 12.5, fontWeight: 500,
                    color: 'var(--text-muted)', letterSpacing: '0.04em', textTransform: 'uppercase'
                  }}>Key milestones</label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {form.milestones.map((m, idx) => (
                      <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{
                          width: 24, height: 24, borderRadius: '50%', background: 'var(--bg-hover)',
                          border: '1px solid var(--border-bright)', fontSize: 11, fontWeight: 700,
                          color: 'var(--text-muted)', display: 'flex', alignItems: 'center',
                          justifyContent: 'center', flexShrink: 0
                        }}>{idx + 1}</div>
                        <input
                          type="text"
                          placeholder="Milestone name"
                          value={m.name}
                          onChange={e => {
                            const updated = [...form.milestones];
                            updated[idx].name = e.target.value;
                            setForm(f => ({ ...f, milestones: updated }));
                          }}
                          style={{ flex: 1 }}
                        />
                        <input
                          type="date"
                          value={m.date}
                          onChange={e => {
                            const updated = [...form.milestones];
                            updated[idx].date = e.target.value;
                            setForm(f => ({ ...f, milestones: updated }));
                          }}
                          style={{ width: 160, flexShrink: 0 }}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const updated = form.milestones.filter((_, i) => i !== idx);
                            setForm(f => ({ ...f, milestones: updated }));
                          }}
                          style={{
                            background: 'none', border: 'none', color: 'var(--accent2)',
                            cursor: 'pointer', padding: 6, borderRadius: 'var(--radius-sm)',
                            transition: 'all 0.15s', flexShrink: 0, display: 'flex',
                          }}
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    ))}

                    <button
                      type="button"
                      onClick={() => setForm(f => ({ ...f, milestones: [...f.milestones, { name: '', date: '' }] }))}
                      style={{
                        width: 'fit-content', color: 'var(--accent)', fontSize: 12, fontWeight: 700,
                        display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, padding: '6px 12px',
                        borderRadius: 'var(--radius-sm)', border: '1px dashed rgba(108,99,255,0.3)',
                        background: 'rgba(108,99,255,0.05)', cursor: 'pointer', transition: 'all 0.15s',
                      }}
                    >
                      <Plus size={14} />
                      Add milestone
                    </button>
                  </div>
                </div>
              </div>

              {/* ACCESS & VISIBILITY */}
              <div className="modern-card" style={{ padding: '32px', marginBottom: '24px' }}>
                <h3 style={{ fontSize: 18, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 24, display: 'flex', alignItems: 'center', gap: 10 }}><Lock size={18} style={{ color: 'var(--accent)' }}/> Access & Visibility</h3>

                <div style={grid3}>
                  <FormField label="Visibility">
                    <select
                      value={form.visibility}
                      onChange={e => setForm(f => ({ ...f, visibility: e.target.value }))}
                    >
                      {['Public - all members', 'Private - invite only'].map(v => (
                        <option key={v} value={v}>{v}</option>
                      ))}
                    </select>
                  </FormField>

                  <FormField label="Default view">
                    <select
                      value={form.default_view}
                      onChange={e => setForm(f => ({ ...f, default_view: e.target.value }))}
                    >
                      {['Board (Kanban)', 'List', 'Timeline'].map(dv => (
                        <option key={dv} value={dv}>{dv}</option>
                      ))}
                    </select>
                  </FormField>

                  <FormField label="Client">
                    <select
                      value={form.client_id}
                      onChange={e => setForm(f => ({ ...f, client_id: e.target.value }))}
                    >
                      <option value="">Select client...</option>
                      {users.filter(u => u.role === 'Client').map(c => (
                        <option key={c.user_id} value={c.user_id}>{c.name}</option>
                      ))}
                    </select>
                  </FormField>
                  
                  <FormField label="Manager">
                    <select
                      value={form.manager_id}
                      onChange={e => setForm(f => ({ ...f, manager_id: e.target.value }))}
                    >
                      <option value="">Select manager...</option>
                      {users.filter(u => u.role === 'Manager').map(m => (
                        <option key={m.user_id} value={m.user_id}>{m.name}</option>
                      ))}
                    </select>
                  </FormField>

                  <FormField label="Client Delivery">
                    <select
                      value={form.client_delivery_id}
                      onChange={e => setForm(f => ({ ...f, client_delivery_id: e.target.value }))}
                    >
                      <option value="">Select client delivery...</option>
                      {users.map(u => (
                        <option key={u.user_id} value={u.user_id}>{u.name}</option>
                      ))}
                    </select>
                  </FormField>
                </div>

                <FormField label="Client Feedback">
                  <textarea
                    rows={3}
                    value={form.client_feedback}
                    onChange={e => setForm(f => ({ ...f, client_feedback: e.target.value }))}
                    placeholder="Enter client feedback for this project..."
                    style={{ resize: 'vertical' }}
                  />
                </FormField>

                <div style={{ marginBottom: 0 }}>
                  <label style={{
                    display: 'block', marginBottom: 8, fontSize: 12.5, fontWeight: 500,
                    color: 'var(--text-muted)', letterSpacing: '0.04em', textTransform: 'uppercase'
                  }}>Tags</label>
                  <div style={{
                    display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center',
                    border: '1px solid var(--border-bright)', background: 'var(--bg)',
                    borderRadius: 'var(--radius-sm)', padding: 8, minHeight: 44,
                  }}>
                    {form.tags.map(tag => (
                      <span key={tag} style={{
                        background: 'var(--bg-hover)', border: '1px solid var(--border-bright)',
                        fontSize: 12, fontWeight: 500, color: 'var(--text)', borderRadius: 20,
                        padding: '4px 10px', display: 'flex', alignItems: 'center', gap: 6,
                      }}>
                        {tag}
                        <button
                          type="button"
                          onClick={() => setForm(f => ({ ...f, tags: f.tags.filter(t => t !== tag) }))}
                          style={{
                            background: 'none', border: 'none', color: 'var(--text-muted)',
                            cursor: 'pointer', padding: 0, display: 'flex',
                          }}
                        >
                          <X size={12} />
                        </button>
                      </span>
                    ))}
                    <input
                      type="text"
                      placeholder="+ Add tag..."
                      value={tagInput}
                      onChange={e => setTagInput(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddTag();
                        }
                      }}
                      style={{
                        background: 'transparent', border: 'none', color: 'var(--text)',
                        fontSize: 12, padding: '2px 6px', flex: 1, minWidth: 120,
                        outline: 'none', boxShadow: 'none',
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* â•â•â•â•â•â•â• Right Column (sidebar) â•â•â•â•â•â•â• */}
              <div style={{ width: 300, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 24 }}>

                {/* Save / Cancel */}
                <div className="modern-card" style={{ padding: '24px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <Button type="submit" style={{ width: '100%', height: 48, fontSize: 15, fontWeight: 700 }}>
                      <Rocket size={18} />
                      {saving ? 'Saving...' : view === 'create' ? 'Launch project' : 'Update project'}
                    </Button>
                    <Button variant="secondary" onClick={() => setView('list')} style={{ width: '100%', height: 44 }}>Cancel</Button>
                  </div>
                </div>

                {/* STATUS */}
                <div className="modern-card" style={{ padding: '24px' }}>
                  <h4 style={{ fontSize: 13, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}><Flag size={16} style={{ color: 'var(--accent)' }}/> Status</h4>
                  <select
                    value={form.status}
                    onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                  >
                    {STATUS_OPTIONS.map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>

                {/* PRIORITY */}
                <div className="modern-card" style={{ padding: '24px' }}>
                  <h4 style={{ fontSize: 13, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}><Activity size={16} style={{ color: 'var(--accent)' }}/> Priority</h4>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {['Low', 'Medium', 'High'].map(p => {
                      const isActive = form.priority === p;
                      const colorMap = { Low: '#34d399', Medium: '#fbbf24', High: '#f87171' };
                      return (
                        <button
                          key={p}
                          type="button"
                          onClick={() => setForm(f => ({ ...f, priority: p }))}
                          style={pillBtn(isActive, colorMap[p])}
                        >
                          {p === 'Medium' ? 'Med' : p}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* PROJECT LEAD */}
                <div className="modern-card" style={{ padding: '24px' }}>
                  <h4 style={{ fontSize: 13, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}><Target size={16} style={{ color: 'var(--accent)' }}/> Project Lead</h4>
                  {showLeadDropdown ? (
                    <select
                      value={form.project_lead_id}
                      onChange={e => {
                        setForm(f => ({ ...f, project_lead_id: e.target.value }));
                        setShowLeadDropdown(false);
                      }}
                      onBlur={() => setTimeout(() => setShowLeadDropdown(false), 200)}
                      autoFocus
                    >
                      <option value="">No Project Lead</option>
                      {users.map(u => (
                        <option key={u.user_id} value={u.user_id}>{u.name} ({u.role})</option>
                      ))}
                    </select>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                      {leadUser ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px', background: 'var(--bg-hover)', borderRadius: 'var(--radius)', border: '1px solid var(--border-bright)' }}>
                          <div style={{
                            width: 40, height: 40, borderRadius: '50%', background: 'rgba(108,99,255,0.1)',
                            color: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontWeight: 700, fontSize: 14,
                          }}>
                            {leadUser.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                          </div>
                          <div>
                            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>{leadUser.name}</div>
                            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{leadUser.role}</div>
                          </div>
                        </div>
                      ) : (
                        <div style={{ padding: '16px', background: 'var(--bg-hover)', borderRadius: 'var(--radius)', border: '1px dashed var(--border-bright)', textAlign: 'center' }}>
                          <span style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic' }}>No lead assigned</span>
                        </div>
                      )}
                      <button type="button" onClick={() => setShowLeadDropdown(true)} style={{ ...smallBtn, justifyContent: 'center', width: '100%', padding: '8px' }}>
                        Change lead
                      </button>
                    </div>
                  )}
                </div>

                {/* MEMBERS */}
                <div className="modern-card" style={{ padding: '24px' }}>
                  <h4 style={{ fontSize: 13, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Users size={16} style={{ color: 'var(--accent)' }}/> Members</div>
                    <button type="button" onClick={() => setShowMembersDropdown(!showMembersDropdown)} style={smallBtn}>
                      <Plus size={12} /> Add
                    </button>
                  </h4>
                  
                  {showMembersDropdown && (
                    <div style={{ marginBottom: 16 }}>
                      <select
                        onChange={e => {
                          const uid = Number(e.target.value);
                          if (uid && !form.members.includes(uid)) {
                            setForm(f => ({ ...f, members: [...f.members, uid] }));
                          }
                          setShowMembersDropdown(false);
                        }}
                        onBlur={() => setTimeout(() => setShowMembersDropdown(false), 200)}
                        autoFocus
                      >
                        <option value="">Select member...</option>
                        {users.filter(u => !form.members.includes(u.user_id)).map(u => (
                          <option key={u.user_id} value={u.user_id}>{u.name} ({u.role})</option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {memberUsers.map(u => (
                      <div key={u.user_id} style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '8px 12px', background: 'var(--bg-hover)', borderRadius: 'var(--radius-sm)',
                        border: '1px solid var(--border-bright)'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{
                            width: 28, height: 28, borderRadius: '50%', background: 'var(--bg-card)',
                            color: 'var(--text-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontWeight: 700, fontSize: 11, border: '1px solid var(--border)'
                          }}>
                            {u.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                          </div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{u.name}</div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setForm(f => ({ ...f, members: f.members.filter(id => id !== u.user_id) }))}
                          style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 4 }}
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                    {memberUsers.length === 0 && (
                      <div style={{ padding: '16px', background: 'var(--bg-hover)', borderRadius: 'var(--radius)', border: '1px dashed var(--border-bright)', textAlign: 'center' }}>
                        <span style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic' }}>No members assigned</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* BUDGET */}
                {(me?.role === 'Admin' || me?.role === 'Client') && (
                  <div className="modern-card" style={{ padding: '24px' }}>
                    <h4 style={{ fontSize: 13, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}><DollarSign size={16} style={{ color: 'var(--accent)' }}/> Budget</h4>
                    <FormField label="Estimated budget">
                      <input
                        type="number"
                        placeholder="e.g. 40,000"
                        value={form.estimated_budget}
                        onChange={e => setForm(f => ({ ...f, estimated_budget: e.target.value }))}
                      />
                    </FormField>
                    <FormField label="Currency">
                      <select
                        value={form.currency}
                        onChange={e => setForm(f => ({ ...f, currency: e.target.value }))}
                      >
                        {['USD ($)', 'EUR (â‚¬)', 'GBP (Â£)', 'INR (â‚¹)'].map(curr => (
                          <option key={curr} value={curr}>{curr}</option>
                        ))}
                      </select>
                    </FormField>
                  </div>
                )}

                 {/* FORM PROGRESS */}
                <div className="modern-card" style={{ padding: '24px' }}>
                  <h4 style={{ fontSize: 13, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}><ListTodo size={16} style={{ color: 'var(--accent)' }}/> Form Progress</h4>
                  <div style={{ fontSize: 13, color: 'var(--text)', fontWeight: 600, marginBottom: 12 }}>
                    {progress.filled} of {progress.total} required fields filled
                  </div>
                  <div style={{
                    background: 'var(--bg-hover)', border: '1px solid var(--border-bright)',
                    width: '100%', height: 8, borderRadius: 20, overflow: 'hidden',
                  }}>
                    <div style={{
                      width: `${progress.percentage}%`, height: '100%', borderRadius: 20,
                      background: 'var(--accent)', transition: 'width 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
                      boxShadow: '0 0 12px rgba(108,99,255,0.4)',
                    }} />
                  </div>
                </div>

              </div>
          </form>
        </div>
      )}

      <Modal open={descriptionModal} onClose={() => setDescriptionModal(false)} title="Project Overview">
        <p style={{ margin: 0, fontSize: 14, color: 'var(--text-muted)', lineHeight: 1.7, fontWeight: 500, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
          {editing?.description || <span style={{ fontStyle: 'italic', color: 'var(--text-dim)' }}>No description provided.</span>}
        </p>
      </Modal>

      <Modal open={Boolean(completionTarget)} onClose={() => setCompletionTarget(null)} title="Complete project">
        <Alert type="error" message={error} />
        <form onSubmit={handleComplete}>
          <FormField label="GitHub commit or pull request URL">
            <input type="url" value={completionForm.completion_url} onChange={e => setCompletionForm(f => ({ ...f, completion_url: e.target.value }))} required placeholder="https://github.com/owner/repository/pull/123" />
          </FormField>
          <FormField label="Completion note">
            <textarea rows={3} value={completionForm.completion_note} onChange={e => setCompletionForm(f => ({ ...f, completion_note: e.target.value }))} placeholder="Briefly describe what was completed" />
          </FormField>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
            <Button variant="secondary" onClick={() => setCompletionTarget(null)}>Cancel</Button>
            <Button type="submit" disabled={completionSaving}>{completionSaving ? 'Submitting...' : 'Mark complete'}</Button>
          </div>
        </form>
      </Modal>

      <Modal open={taskModal} onClose={() => setTaskModal(false)} title={editingTask ? 'Edit Task' : 'New Task'}>
        <Alert type="error" message={taskError} />
        <form onSubmit={handleTaskSave}>
          <FormField label="Task Name">
            <input value={taskForm.task_name} onChange={e => setTaskForm(f => ({...f, task_name: e.target.value}))} required placeholder="Task name" />
          </FormField>
          <FormField label="Description">
            <textarea rows={3} value={taskForm.description} onChange={e => setTaskForm(f => ({...f, description: e.target.value}))} placeholder="Optional description..." style={{ resize: 'vertical' }} />
          </FormField>
          <FormField label="Assign To">
            <select value={taskForm.assigned_to} onChange={e => setTaskForm(f => ({...f, assigned_to: e.target.value}))}>
              <option value="">Unassigned</option>
              {users.map(u => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
            </select>
          </FormField>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <FormField label="Status">
              <select value={taskForm.status} onChange={e => setTaskForm(f => ({...f, status: e.target.value}))}>
                {['Backlog', 'To Do','In Progress','In Review','Done'].map(s => <option key={s}>{s}</option>)}
              </select>
            </FormField>
            <FormField label="Priority">
              <select value={taskForm.priority} onChange={e => setTaskForm(f => ({...f, priority: e.target.value}))}>
                {['Low','Medium','High','Critical'].map(s => <option key={s}>{s}</option>)}
              </select>
            </FormField>
          </div>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 16 }}>
            <Button variant="secondary" onClick={() => setTaskModal(false)} type="button">Cancel</Button>
            <Button type="submit" disabled={taskSaving}>{taskSaving ? 'Saving...' : editingTask ? 'Update' : 'Create'}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}